/**
 * Elite Batch 14 Part 4 — Workflow Templates 1308–1327
 * Customer success playbook, support triage, DPIA assessment, news digest,
 * daily standup bots, data entry automation, migration validation, privacy
 * request handling, data quality scoring, data sync, deadline alerts,
 * deadline reminders, delivery exception handling, delivery window selection,
 * demo request, deploy notification, document approval, and document classification.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 14 PART 4 — TEMPLATES 1308–1327
// =============================================================================

export const ELITE_BATCH14_PART4: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1308. CUSTOMER SUCCESS PLAYBOOK BOT (8 nodes)
  // ===================================================================

  "Customer Success Playbook Bot": {
    summary:
      "Captures customer lifecycle events via webhook, extracts customer context with AI, selects the appropriate success playbook, posts conversational guidance to Slack, generates a personalized email template, creates an action item in Asana, waits 14 days for outcome measurement, and routes based on outcome results.",
    nodes: [
      trigger("Webhook: Customer lifecycle event", "webhook_app", {
        source: "customer_platform",
        event: "lifecycle.stage_changed",
        howItStarts:
          "Fires when a customer lifecycle event is received (onboarding complete, usage drop, renewal approaching, expansion signal, or churn risk detected)",
        setup:
          "Configure webhook from your customer success platform (Gainsight, ChurnZero, Vitally, etc.). Set up OpenAI API key for playbook selection and email generation. Configure Slack webhook for CS team channel. Set up Asana API token for task creation. Provide Google Sheets for outcome tracking.",
        inputFields: [],
      }),

      // 2. AI extract customer context
      aiNode(
        "2",
        "AI: Extract customer context",
        280,
        "You are a customer success data analyst. Extract the customer details from the raw webhook payload regardless of format. Find the customer name, email, company name, current lifecycle stage, previous stage, account health score, MRR value, days since onboarding, product usage metrics, and the specific event that triggered this lifecycle change. Respond JSON: {customer_name: string, customer_email: string, company_name: string, current_stage: string, previous_stage: string, health_score: number, mrr_value: string, days_since_onboard: number, usage_summary: string, trigger_event: string}",
        "Raw lifecycle event payload: {webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "current_stage", as: "current_stage", type: "string" },
          { field: "previous_stage", as: "previous_stage", type: "string" },
          { field: "health_score", as: "health_score", type: "number" },
          { field: "mrr_value", as: "mrr_value", type: "string" },
          {
            field: "days_since_onboard",
            as: "days_since_onboard",
            type: "number",
          },
          { field: "usage_summary", as: "usage_summary", type: "string" },
          { field: "trigger_event", as: "trigger_event", type: "string" },
        ],
      ),

      // 3. AI playbook selection
      aiNode(
        "3",
        "AI: Select success playbook",
        520,
        "You are a senior customer success strategist. Based on the customer context, select the most appropriate playbook and define the next 3 action steps. Consider the lifecycle stage, health score, MRR, usage patterns, and trigger event. Playbook options: onboarding_acceleration, adoption_boost, expansion_opportunity, renewal_prep, churn_prevention, advocacy_cultivation, escalation_recovery. Respond JSON: {playbook_name: string, playbook_priority: string, action_step_1: string, action_step_2: string, action_step_3: string, csm_talking_points: string, risk_level: string, recommended_touchpoint: string}",
        "Customer: {customer_name} at {company_name}. Stage: {current_stage} (was {previous_stage}). Health: {health_score}/100. MRR: ${mrr_value}. Days onboarded: {days_since_onboard}. Usage: {usage_summary}. Trigger: {trigger_event}.",
        [
          { field: "playbook_name", as: "playbook_name", type: "string" },
          {
            field: "playbook_priority",
            as: "playbook_priority",
            type: "string",
          },
          { field: "action_step_1", as: "action_step_1", type: "string" },
          { field: "action_step_2", as: "action_step_2", type: "string" },
          { field: "action_step_3", as: "action_step_3", type: "string" },
          {
            field: "csm_talking_points",
            as: "csm_talking_points",
            type: "string",
          },
          { field: "risk_level", as: "risk_level", type: "string" },
          {
            field: "recommended_touchpoint",
            as: "recommended_touchpoint",
            type: "string",
          },
        ],
      ),

      // 4. Slack guidance post
      slackNode(
        "4",
        "Post playbook guidance to CS Slack",
        760,
        ":book: *CS Playbook Activated: {playbook_name}*\n*Customer:* {customer_name} ({company_name})\n*Stage:* {previous_stage} → {current_stage} | Health: {health_score}/100\n*MRR:* ${mrr_value} | Risk: {risk_level} | Priority: {playbook_priority}\n*Touchpoint:* {recommended_touchpoint}\n\n*Action Steps:*\n1. {action_step_1}\n2. {action_step_2}\n3. {action_step_3}\n\n*Talking Points:* {csm_talking_points}",
      ),

      // 5. AI email template generation
      aiNode(
        "5",
        "AI: Generate personalized CS email",
        1000,
        "You are a customer success email specialist. Write a personalized, warm email to the customer based on the playbook and their context. The tone should be helpful and proactive, not salesy. Include specific references to their usage and a clear call-to-action aligned with the playbook. Respond JSON: {email_subject: string, email_body: string}",
        "Customer: {customer_name}. Company: {company_name}. Playbook: {playbook_name}. Priority: {playbook_priority}. Touchpoint: {recommended_touchpoint}. Usage: {usage_summary}. Action steps: 1) {action_step_1} 2) {action_step_2} 3) {action_step_3}. Talking points: {csm_talking_points}.",
        [
          { field: "email_subject", as: "cs_email_subject", type: "string" },
          { field: "email_body", as: "cs_email_body", type: "string" },
        ],
      ),

      // 6. Action tracking in Asana
      n("6", "hitApi", "Create Asana action task", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"[{playbook_name}] {company_name} - {customer_name}","notes":"Priority: {playbook_priority}\\nRisk: {risk_level}\\nMRR: ${mrr_value}\\nHealth: {health_score}/100\\n\\nActions:\\n1. {action_step_1}\\n2. {action_step_2}\\n3. {action_step_3}","projects":["{asana_cs_project_id}"],"due_on":"{due_date_14d}"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. Wait 14 days for outcome
      n("7", "delayAction", "Wait 14 days for outcome measurement", 1480, {
        delayDetails: { duration: 14, unit: "days" },
      }),

      // 8. Outcome monitoring condition
      n("8", "condition", "Check: Did health score improve?", 1720, {
        conditionData: [
          { variable: "health_score", operator: "greater_than", value: "60" },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1309. CUSTOMER SUPPORT TRIAGE (7 nodes)
  // ===================================================================

  "Customer Support Triage": {
    summary:
      "Receives support ticket creation events via webhook, classifies the ticket with AI to determine priority and category, enriches the customer profile from HubSpot, routes based on priority level, sends an instant acknowledgment email, assigns the ticket to the right agent via Slack, and logs SLA tracking data to Sheets.",
    nodes: [
      trigger("Webhook: Support ticket created", "webhook_app", {
        source: "support_platform",
        event: "ticket.created",
        howItStarts:
          "Fires when a new support ticket is created from any channel (email, chat, form, phone) and needs to be classified, prioritized, and routed to the correct support agent",
        setup:
          "Configure webhook from your helpdesk (Zendesk, Intercom, Freshdesk, etc.). Set up OpenAI API key for ticket classification. Configure HubSpot for customer enrichment. Set up Slack webhook for agent routing. Provide Google Sheets for SLA tracking.",
        inputFields: [],
      }),

      // 2. AI ticket classification
      aiNode(
        "2",
        "AI: Classify and prioritize ticket",
        280,
        "You are a senior support operations analyst. Analyze the raw ticket webhook payload and extract the customer email, subject, description, channel, and any metadata. Classify the ticket into a category (billing, technical, feature_request, account, onboarding, bug_report, security) and assign a priority (P1_critical, P2_high, P3_medium, P4_low). Determine the required skill set for routing. Respond JSON: {customer_email: string, customer_name: string, ticket_subject: string, ticket_description: string, ticket_channel: string, category: string, priority: string, required_skill: string, sentiment: string, is_escalation: boolean, suggested_response: string}",
        "Raw ticket webhook data: {webhook_payload}",
        [
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "ticket_subject", as: "ticket_subject", type: "string" },
          {
            field: "ticket_description",
            as: "ticket_description",
            type: "string",
          },
          { field: "ticket_channel", as: "ticket_channel", type: "string" },
          { field: "category", as: "ticket_category", type: "string" },
          { field: "priority", as: "ticket_priority", type: "string" },
          { field: "required_skill", as: "required_skill", type: "string" },
          { field: "sentiment", as: "customer_sentiment", type: "string" },
          { field: "is_escalation", as: "is_escalation", type: "boolean" },
          {
            field: "suggested_response",
            as: "suggested_response",
            type: "string",
          },
        ],
      ),

      // 3. Customer context enrichment from HubSpot
      hubspotGetNode(
        "3",
        "Enrich customer from HubSpot",
        520,
        "crm/v3/objects/contacts/search",
        [
          {
            name: "results[0].properties.lifecyclestage",
            customName: "customer_lifecycle",
            type: "string",
          },
          {
            name: "results[0].properties.hs_object_id",
            customName: "hubspot_contact_id",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "customer_company",
            type: "string",
          },
          {
            name: "results[0].properties.hs_lead_status",
            customName: "customer_status",
            type: "string",
          },
          { name: "total", customName: "contact_match_count", type: "number" },
        ],
      ),

      // 4. Priority routing condition
      n("4", "condition", "Check: Is ticket P1 or P2 priority?", 760, {
        conditionData: [
          { variable: "ticket_priority", operator: "contains", value: "P1" },
        ],
      }),

      // 5. Instant acknowledgment email
      emailNode(
        "5",
        "Send instant acknowledgment email",
        1000,
        "{customer_email}",
        "We received your request: {ticket_subject}",
        "<p>Hi {customer_name},</p><p>We have received your support request and it has been assigned priority <strong>{ticket_priority}</strong> in the <strong>{ticket_category}</strong> category.</p><p>A specialist with <strong>{required_skill}</strong> expertise will be reaching out shortly. Our SLA for {ticket_priority} tickets is within the next business hours window.</p><p>In the meantime, here is a preliminary thought:<br/>{suggested_response}</p><p>Thank you for your patience.</p>",
        380,
      ),

      // 6. Agent assignment via Slack
      slackNode(
        "6",
        "Route ticket to support agent (Slack)",
        1000,
        ":ticket: *New Ticket — {ticket_priority}*\n*Subject:* {ticket_subject}\n*Customer:* {customer_name} ({customer_email}) — {customer_company}\n*Category:* {ticket_category} | Skill needed: {required_skill}\n*Channel:* {ticket_channel} | Sentiment: {customer_sentiment}\n*Lifecycle:* {customer_lifecycle} | Escalation: {is_escalation}\n\n*Description:* {ticket_description}\n\n*Suggested response:* {suggested_response}",
        -220,
      ),

      // 7. SLA tracking in Sheets
      sheetsNode(
        "7",
        "Log SLA tracking data (Sheets)",
        1240,
        "SLA_Tracking",
        '[["{customer_email}","{customer_name}","{customer_company}","{ticket_subject}","{ticket_category}","{ticket_priority}","{required_skill}","{ticket_channel}","{customer_sentiment}","{is_escalation}","{customer_lifecycle}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1310. DPIA ASSESSMENT BOT (8 nodes)
  // ===================================================================

  "DPIA Assessment Bot": {
    summary:
      "Receives DPIA (Data Protection Impact Assessment) requests via webhook, uses AI to generate a targeted questionnaire, collects and structures data via AI analysis, calculates risk scores in code, generates AI-powered mitigation recommendations, compiles the DPIA report in code, routes to the DPO for approval, and logs the assessment to the DPIA register in Sheets.",
    nodes: [
      trigger("Webhook: DPIA request submitted", "webhook_app", {
        source: "compliance_system",
        event: "dpia.request_created",
        howItStarts:
          "Fires when a new Data Protection Impact Assessment request is submitted, typically triggered by a new data processing activity, system change, or regulatory requirement",
        setup:
          "Configure webhook from your compliance or GRC platform. Set up OpenAI API key for questionnaire generation and risk analysis. Provide Google Sheets for DPIA register. Configure email for DPO notification. Define risk thresholds for your organization.",
        inputFields: [],
      }),

      // 2. AI questionnaire generation
      aiNode(
        "2",
        "AI: Extract DPIA context and generate questionnaire",
        280,
        "You are a data protection specialist. Analyze the raw webhook payload to extract the project name, requester name, requester email, department, processing purpose, data categories, data subjects, legal basis, and any initial description. Then generate 5 targeted assessment questions based on the specific processing activity. Respond JSON: {project_name: string, requester_name: string, requester_email: string, department: string, processing_purpose: string, data_categories: string, data_subjects: string, legal_basis: string, question_1: string, question_2: string, question_3: string, question_4: string, question_5: string}",
        "DPIA request payload: {webhook_payload}",
        [
          { field: "project_name", as: "project_name", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "department", as: "department", type: "string" },
          {
            field: "processing_purpose",
            as: "processing_purpose",
            type: "string",
          },
          { field: "data_categories", as: "data_categories", type: "string" },
          { field: "data_subjects", as: "data_subjects", type: "string" },
          { field: "legal_basis", as: "legal_basis", type: "string" },
          { field: "question_1", as: "dpia_q1", type: "string" },
          { field: "question_2", as: "dpia_q2", type: "string" },
          { field: "question_3", as: "dpia_q3", type: "string" },
          { field: "question_4", as: "dpia_q4", type: "string" },
          { field: "question_5", as: "dpia_q5", type: "string" },
        ],
      ),

      // 3. AI conversational data collection and analysis
      aiNode(
        "3",
        "AI: Analyze processing activity risks",
        520,
        "You are a privacy risk analyst. Based on the DPIA context, assess the inherent risks of the data processing activity. Evaluate: data volume, sensitivity level, cross-border transfers, automated decision-making, profiling, vulnerable data subjects, novel technology use, and large-scale processing. Assign risk scores (1-10) for likelihood and impact. Respond JSON: {sensitivity_level: string, cross_border: boolean, automated_decisions: boolean, profiling_risk: boolean, vulnerable_subjects: boolean, novel_technology: boolean, large_scale: boolean, likelihood_score: number, impact_score: number, inherent_risk_level: string, key_risks: string}",
        "Project: {project_name}. Purpose: {processing_purpose}. Data categories: {data_categories}. Data subjects: {data_subjects}. Legal basis: {legal_basis}. Department: {department}.",
        [
          {
            field: "sensitivity_level",
            as: "sensitivity_level",
            type: "string",
          },
          { field: "cross_border", as: "cross_border", type: "boolean" },
          {
            field: "automated_decisions",
            as: "automated_decisions",
            type: "boolean",
          },
          { field: "likelihood_score", as: "likelihood_score", type: "number" },
          { field: "impact_score", as: "impact_score", type: "number" },
          {
            field: "inherent_risk_level",
            as: "inherent_risk_level",
            type: "string",
          },
          { field: "key_risks", as: "key_risks", type: "string" },
        ],
      ),

      // 4. Risk assessment scoring (runCode)
      n("4", "runCode", "Calculate DPIA risk scores", 760, {
        runCodeDetails: {
          code: `// Calculate composite DPIA risk scores
const likelihood = Number(variables.likelihood_score) || 5;
const impact = Number(variables.impact_score) || 5;
const riskScore = ((likelihood * impact) / 10).toFixed(1);
const crossBorder = variables.cross_border === 'true' || variables.cross_border === true;
const automated = variables.automated_decisions === 'true' || variables.automated_decisions === true;
let riskMultiplier = 1.0;
if (crossBorder) riskMultiplier += 0.3;
if (automated) riskMultiplier += 0.4;
const adjustedScore = (parseFloat(riskScore) * riskMultiplier).toFixed(1);
const riskCategory = parseFloat(adjustedScore) >= 7 ? 'HIGH' : parseFloat(adjustedScore) >= 4 ? 'MEDIUM' : 'LOW';
const requiresDpoReview = riskCategory === 'HIGH' || crossBorder || automated;
const assessmentId = 'DPIA-' + Date.now().toString(36).toUpperCase();
return { risk_score: riskScore, adjusted_risk_score: adjustedScore, risk_category: riskCategory, risk_multiplier: riskMultiplier.toFixed(1), requires_dpo_review: requiresDpoReview, assessment_id: assessmentId };`,
          outputs: [
            { name: "risk_score", type: "string" },
            { name: "adjusted_risk_score", type: "string" },
            { name: "risk_category", type: "string" },
            { name: "risk_multiplier", type: "string" },
            { name: "requires_dpo_review", type: "boolean" },
            { name: "assessment_id", type: "string" },
          ],
        },
      }),

      // 5. AI mitigation recommendation
      aiNode(
        "5",
        "AI: Generate mitigation recommendations",
        1000,
        "You are a data protection officer assistant. Based on the identified risks, generate specific, actionable mitigation measures. For each key risk, provide a concrete control or safeguard. Include technical measures (encryption, pseudonymization, access controls), organizational measures (policies, training, DPIAs), and contractual measures (DPAs, SCCs) as appropriate. Respond JSON: {mitigation_measures: string, technical_controls: string, organizational_controls: string, contractual_controls: string, residual_risk_level: string, dpo_recommendation: string}",
        "Project: {project_name}. Risk category: {risk_category}. Adjusted score: {adjusted_risk_score}. Key risks: {key_risks}. Sensitivity: {sensitivity_level}. Cross-border: {cross_border}. Automated decisions: {automated_decisions}. Data categories: {data_categories}. Data subjects: {data_subjects}.",
        [
          {
            field: "mitigation_measures",
            as: "mitigation_measures",
            type: "string",
          },
          {
            field: "technical_controls",
            as: "technical_controls",
            type: "string",
          },
          {
            field: "organizational_controls",
            as: "organizational_controls",
            type: "string",
          },
          {
            field: "contractual_controls",
            as: "contractual_controls",
            type: "string",
          },
          {
            field: "residual_risk_level",
            as: "residual_risk_level",
            type: "string",
          },
          {
            field: "dpo_recommendation",
            as: "dpo_recommendation",
            type: "string",
          },
        ],
      ),

      // 6. DPIA report generation (runCode)
      n("6", "runCode", "Compile DPIA report", 1240, {
        runCodeDetails: {
          code: `// Compile structured DPIA report
const report = {
  id: variables.assessment_id,
  project: variables.project_name,
  requester: variables.requester_name + ' (' + variables.department + ')',
  purpose: variables.processing_purpose,
  data_categories: variables.data_categories,
  data_subjects: variables.data_subjects,
  legal_basis: variables.legal_basis,
  risk_assessment: {
    inherent: variables.inherent_risk_level,
    score: variables.adjusted_risk_score,
    category: variables.risk_category
  },
  mitigations: variables.mitigation_measures,
  residual_risk: variables.residual_risk_level,
  recommendation: variables.dpo_recommendation
};
const reportHtml = '<h2>DPIA Report: ' + variables.assessment_id + '</h2>' +
  '<p><strong>Project:</strong> ' + variables.project_name + '</p>' +
  '<p><strong>Risk Level:</strong> ' + variables.risk_category + ' (Score: ' + variables.adjusted_risk_score + ')</p>' +
  '<p><strong>Residual Risk:</strong> ' + variables.residual_risk_level + '</p>' +
  '<h3>Mitigations</h3><p>' + variables.mitigation_measures + '</p>' +
  '<h3>DPO Recommendation</h3><p>' + variables.dpo_recommendation + '</p>';
return { report_json: JSON.stringify(report), report_html: reportHtml, report_status: 'pending_review' };`,
          outputs: [
            { name: "report_json", type: "string" },
            { name: "report_html", type: "string" },
            { name: "report_status", type: "string" },
          ],
        },
      }),

      // 7. DPO review routing (approval gate)
      n("7", "approvalGate", "DPO: Review and approve DPIA", 1480, {
        approvalGateData: {
          message:
            "DPIA Review Required — {assessment_id}\n\nProject: {project_name}\nRequester: {requester_name} ({department})\nPurpose: {processing_purpose}\nData Categories: {data_categories}\nData Subjects: {data_subjects}\nLegal Basis: {legal_basis}\n\nRisk Category: {risk_category} (Score: {adjusted_risk_score})\nInherent Risk: {inherent_risk_level}\nResidual Risk: {residual_risk_level}\nKey Risks: {key_risks}\n\nMitigation Measures:\n{mitigation_measures}\n\nTechnical Controls: {technical_controls}\nOrganizational Controls: {organizational_controls}\nContractual Controls: {contractual_controls}\n\nDPO Recommendation: {dpo_recommendation}",
        },
      }),

      // 8. DPIA register logging (Sheets)
      sheetsNode(
        "8",
        "Log to DPIA register (Sheets)",
        1720,
        "DPIA_Register",
        '[["{assessment_id}","{project_name}","{requester_name}","{department}","{processing_purpose}","{data_categories}","{data_subjects}","{legal_basis}","{risk_category}","{adjusted_risk_score}","{residual_risk_level}","{dpo_recommendation}","{report_status}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1311. DAILY NEWS DIGEST GENERATOR (7 nodes)
  // ===================================================================

  "Daily News Digest Generator": {
    summary:
      "Runs on a morning schedule to fetch news from multiple sources via API, filters for relevance with AI, generates summaries and analysis, adapts content to brand voice in code, compiles and sends the digest via email, and distributes a summary to Slack.",
    nodes: [
      trigger("Schedule: Morning news collection", "schedule", {
        schedule: "0 6 * * 1-5",
        howItStarts:
          "Fires every weekday at 6 AM to collect news from multiple sources, filter for industry relevance, and compile a curated digest for the team",
        setup:
          "Configure NewsAPI key for multi-source news collection. Set up OpenAI API key for relevance filtering and summarization. Define industry keywords and topics of interest. Configure email distribution list. Set up Slack webhook for team channel.",
        inputFields: [],
      }),

      // 2. Multi-source news collection API
      n("2", "hitApi", "Fetch news from NewsAPI", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://newsapi.org/v2/everything?q={industry_keywords}&sortBy=publishedAt&pageSize=30&language=en&apiKey={newsapi_key}",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "totalResults",
              customName: "total_articles_found",
              type: "number",
            },
            { name: "articles", customName: "raw_articles", type: "string" },
            {
              name: "articles[0].title",
              customName: "first_article_title",
              type: "string",
            },
            {
              name: "articles[0].source.name",
              customName: "first_source_name",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI relevance filtering
      aiNode(
        "3",
        "AI: Filter and rank by relevance",
        520,
        "You are a news editor and industry analyst. Review the raw articles list and filter for the most relevant articles to our business. Score each article 1-10 for relevance, discard anything below 6. Select the top 8 most impactful articles. For each selected article, note why it matters to our industry. Respond JSON: {selected_count: number, article_titles: string, article_summaries: string, top_story_title: string, top_story_url: string, relevance_scores: string, trend_themes: string}",
        "Industry focus: {industry_keywords}. Raw articles data: {raw_articles}. Total found: {total_articles_found}.",
        [
          { field: "selected_count", as: "selected_count", type: "number" },
          { field: "article_titles", as: "article_titles", type: "string" },
          {
            field: "article_summaries",
            as: "article_summaries",
            type: "string",
          },
          { field: "top_story_title", as: "top_story_title", type: "string" },
          { field: "top_story_url", as: "top_story_url", type: "string" },
          { field: "relevance_scores", as: "relevance_scores", type: "string" },
          { field: "trend_themes", as: "trend_themes", type: "string" },
        ],
      ),

      // 4. AI summary and analysis
      aiNode(
        "4",
        "AI: Generate executive summary and analysis",
        760,
        "You are a business intelligence analyst. Write an executive summary of today's key news. Include: a one-paragraph overview of the day's top themes, individual article summaries (2-3 sentences each), and a 'What This Means For Us' section with strategic implications. Keep the tone professional and insight-driven. Respond JSON: {executive_overview: string, individual_summaries_html: string, strategic_implications: string, action_items: string}",
        "Selected articles: {article_titles}. Summaries: {article_summaries}. Top story: {top_story_title}. Trend themes: {trend_themes}. Relevance scores: {relevance_scores}.",
        [
          {
            field: "executive_overview",
            as: "executive_overview",
            type: "string",
          },
          {
            field: "individual_summaries_html",
            as: "individual_summaries_html",
            type: "string",
          },
          {
            field: "strategic_implications",
            as: "strategic_implications",
            type: "string",
          },
          { field: "action_items", as: "action_items", type: "string" },
        ],
      ),

      // 5. Brand voice adaptation (runCode)
      n("5", "runCode", "Adapt digest to brand voice", 1000, {
        runCodeDetails: {
          code: `// Format digest with brand styling and metadata
const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
const selectedCount = Number(variables.selected_count) || 0;
const digestHtml = '<div style="font-family:Arial,sans-serif;max-width:680px;margin:0 auto;">' +
  '<h1 style="color:#1a1a2e;">Daily Intelligence Briefing</h1>' +
  '<p style="color:#666;">' + today + ' | ' + selectedCount + ' curated stories</p>' +
  '<hr style="border-color:#e0e0e0;">' +
  '<h2>Executive Overview</h2><p>' + (variables.executive_overview || '') + '</p>' +
  '<h2>Top Stories</h2>' + (variables.individual_summaries_html || '') +
  '<h2>Strategic Implications</h2><p>' + (variables.strategic_implications || '') + '</p>' +
  '<h2>Recommended Actions</h2><p>' + (variables.action_items || '') + '</p>' +
  '<hr style="border-color:#e0e0e0;"><p style="color:#999;font-size:12px;">Curated by AI from ' + (variables.total_articles_found || 0) + ' sources</p></div>';
const slackSummary = '*Daily Intelligence Briefing — ' + today + '*\\n' + selectedCount + ' stories curated\\n\\n' +
  '*Top Story:* ' + (variables.top_story_title || 'N/A') + '\\n' +
  '*Themes:* ' + (variables.trend_themes || 'N/A') + '\\n' +
  '*Key Implication:* ' + (variables.strategic_implications || '').substring(0, 200);
return { digest_html: digestHtml, digest_date: today, slack_summary: slackSummary };`,
          outputs: [
            { name: "digest_html", type: "string" },
            { name: "digest_date", type: "string" },
            { name: "slack_summary", type: "string" },
          ],
        },
      }),

      // 6. Email digest distribution
      emailNode(
        "6",
        "Send daily digest email",
        1240,
        "{digest_distribution_list}",
        "Daily Intelligence Briefing — {digest_date} | {top_story_title}",
        "{digest_html}",
      ),

      // 7. Slack distribution
      slackNode("7", "Post digest summary to Slack", 1480, "{slack_summary}"),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("5", "7"),
    ],
  },

  // ===================================================================
  // 1312. DAILY STANDUP BOT (7 nodes)
  // ===================================================================

  "Daily Standup Bot": {
    summary:
      "Triggers on a morning schedule, checks the team roster via API, sends standup prompts to Slack, waits 2 hours for responses, compiles responses with AI, posts the compiled digest to Slack, and routes blockers for escalation based on a condition check.",
    nodes: [
      trigger("Schedule: Morning standup prompt", "schedule", {
        schedule: "0 9 * * 1-5",
        howItStarts:
          "Fires every weekday at 9 AM to prompt team members for their daily standup updates, collect responses, and compile a team digest",
        setup:
          "Configure Slack webhook for standup prompts and digest posting. Set up team roster API endpoint or define team members. Configure OpenAI API key for response compilation. Set response window to 2 hours.",
        inputFields: [],
      }),

      // 2. Team roster check API
      n("2", "hitApi", "Fetch team roster from HR API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bamboohr.com/api/gateway.php/{bamboo_subdomain}/v1/employees/directory",
          headers: [
            { key: "Authorization", value: "Basic {bamboohr_api_key_base64}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "employees",
              customName: "team_roster_raw",
              type: "string",
            },
            {
              name: "employees.length",
              customName: "team_size",
              type: "number",
            },
          ],
        },
      }),

      // 3. Standup prompt to Slack
      slackNode(
        "3",
        "Send standup prompt to Slack",
        520,
        ":sunrise: *Daily Standup — {standup_date}*\nGood morning team! Please share your update:\n\n1. What did you accomplish yesterday?\n2. What are you working on today?\n3. Any blockers or things you need help with?\n\nPlease reply in-thread within the next 2 hours. Team size: {team_size} members.",
      ),

      // 4. Delay 2 hours for responses
      n("4", "delayAction", "Wait 2 hours for standup responses", 760, {
        delayDetails: { duration: 2, unit: "hours" },
      }),

      // 5. AI standup compilation
      aiNode(
        "5",
        "AI: Compile standup responses",
        1000,
        "You are a scrum master assistant. Compile the team standup responses into a structured digest. Group by team member. Identify any blockers, dependencies between team members, and recurring themes. Flag critical blockers that need immediate attention. Calculate response rate. Respond JSON: {digest_text: string, total_responses: number, response_rate_pct: number, blockers_found: number, blocker_details: string, critical_blockers: boolean, key_themes: string, cross_dependencies: string}",
        "Team size: {team_size}. Team roster: {team_roster_raw}. Standup responses from Slack thread: {slack_thread_responses}. Date: {standup_date}.",
        [
          { field: "digest_text", as: "standup_digest", type: "string" },
          { field: "total_responses", as: "total_responses", type: "number" },
          { field: "response_rate_pct", as: "response_rate", type: "number" },
          { field: "blockers_found", as: "blockers_found", type: "number" },
          { field: "blocker_details", as: "blocker_details", type: "string" },
          {
            field: "critical_blockers",
            as: "critical_blockers",
            type: "boolean",
          },
          { field: "key_themes", as: "key_themes", type: "string" },
          {
            field: "cross_dependencies",
            as: "cross_dependencies",
            type: "string",
          },
        ],
      ),

      // 6. Digest posting to Slack
      slackNode(
        "6",
        "Post standup digest to Slack",
        1240,
        ":clipboard: *Standup Digest — {standup_date}*\nResponse rate: {response_rate}% ({total_responses}/{team_size})\n\n{standup_digest}\n\n:warning: *Blockers ({blockers_found}):* {blocker_details}\n:link: *Dependencies:* {cross_dependencies}\n:bulb: *Key Themes:* {key_themes}",
      ),

      // 7. Blocker escalation condition
      n("7", "condition", "Check: Are there critical blockers?", 1480, {
        conditionData: [
          { variable: "critical_blockers", operator: "equals", value: "true" },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1313. DAILY STANDUP COLLECTOR BOT (7 nodes)
  // ===================================================================

  "Daily Standup Collector Bot": {
    summary:
      "Triggers daily on schedule, enumerates team members in code, sends personalized conversational prompts via Slack, waits 2 hours for collection, parses individual responses with smart extraction in code, compiles an AI-driven digest, and distributes the final summary to Slack.",
    nodes: [
      trigger("Schedule: Daily standup collection", "schedule", {
        schedule: "0 9 * * 1-5",
        howItStarts:
          "Fires every weekday at 9 AM to send personalized standup prompts to each team member via DM and collect their structured responses",
        setup:
          "Configure Slack Bot token (not webhook) for sending DMs. Define team member list with Slack user IDs. Set up OpenAI API key for digest compilation. Configure response timeout (default 2 hours).",
        inputFields: [],
      }),

      // 2. Team member enumeration (runCode)
      n("2", "runCode", "Enumerate team members for prompts", 280, {
        runCodeDetails: {
          code: `// Build team member list with Slack IDs for individual prompts
const teamConfig = variables.team_config || '[]';
let members;
try { members = JSON.parse(teamConfig); } catch(e) { members = []; }
if (members.length === 0) {
  members = [
    { name: 'Team Member', slack_id: 'U_DEFAULT', role: 'engineer' }
  ];
}
const memberNames = members.map(m => m.name).join(', ');
const memberCount = members.length;
const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
const promptTemplate = 'Hi! Time for your daily standup. Please share:\\n1. What you completed yesterday\\n2. What you are working on today\\n3. Any blockers (or type \\"none\\")';
return { team_members_json: JSON.stringify(members), member_names: memberNames, member_count: memberCount, standup_date: today, prompt_template: promptTemplate };`,
          outputs: [
            { name: "team_members_json", type: "string" },
            { name: "member_names", type: "string" },
            { name: "member_count", type: "number" },
            { name: "standup_date", type: "string" },
            { name: "prompt_template", type: "string" },
          ],
        },
      }),

      // 3. Conversational prompt via Slack
      slackNode(
        "3",
        "Send standup prompts via Slack",
        520,
        ":wave: *Standup Time — {standup_date}*\nSending individual prompts to {member_count} team members: {member_names}\n\n{prompt_template}\n\nResponses will be compiled in 2 hours.",
      ),

      // 4. Delay 2 hours for collection
      n("4", "delayAction", "Wait 2 hours for responses", 760, {
        delayDetails: { duration: 2, unit: "hours" },
      }),

      // 5. Smart response parsing (runCode)
      n("5", "runCode", "Parse standup responses", 1000, {
        runCodeDetails: {
          code: `// Parse and structure individual standup responses
const memberCount = Number(variables.member_count) || 0;
const rawResponses = variables.collected_responses || '';
const lines = rawResponses.split('\\n').filter(l => l.trim());
let parsed = [];
let currentMember = null;
let respondedCount = 0;
const blockers = [];
lines.forEach(line => {
  const memberMatch = line.match(/^\\*?([A-Za-z ]+?)\\*?:/);
  if (memberMatch) {
    currentMember = memberMatch[1].trim();
    respondedCount++;
  }
  if (line.toLowerCase().includes('blocker') && !line.toLowerCase().includes('none')) {
    blockers.push((currentMember || 'Unknown') + ': ' + line.replace(/.*blocker[s]?:?/i, '').trim());
  }
});
const responseRate = memberCount > 0 ? ((respondedCount / memberCount) * 100).toFixed(0) : '0';
const missingCount = memberCount - respondedCount;
const blockerSummary = blockers.length > 0 ? blockers.join('; ') : 'No blockers reported';
const hasBlockers = blockers.length > 0;
return { responded_count: respondedCount, response_rate: responseRate, missing_count: missingCount, blocker_summary: blockerSummary, blocker_count: blockers.length, has_blockers: hasBlockers };`,
          outputs: [
            { name: "responded_count", type: "number" },
            { name: "response_rate", type: "string" },
            { name: "missing_count", type: "number" },
            { name: "blocker_summary", type: "string" },
            { name: "blocker_count", type: "number" },
            { name: "has_blockers", type: "boolean" },
          ],
        },
      }),

      // 6. AI digest compilation
      aiNode(
        "6",
        "AI: Compile team standup digest",
        1240,
        "You are a scrum master. Compile a clean, organized standup digest from the parsed responses. Group updates by team member with clear sections for Yesterday, Today, and Blockers. Highlight any blockers prominently. Add a brief team health summary based on response rate and blocker count. Respond JSON: {formatted_digest: string, health_summary: string, action_items: string}",
        "Date: {standup_date}. Team: {member_names}. Response rate: {response_rate}% ({responded_count}/{member_count}). Missing: {missing_count}. Blockers: {blocker_summary}. Raw responses: {collected_responses}.",
        [
          { field: "formatted_digest", as: "formatted_digest", type: "string" },
          {
            field: "health_summary",
            as: "team_health_summary",
            type: "string",
          },
          { field: "action_items", as: "standup_action_items", type: "string" },
        ],
      ),

      // 7. Digest distribution via Slack
      slackNode(
        "7",
        "Post compiled digest to Slack",
        1480,
        ":memo: *Team Standup Digest — {standup_date}*\n*Response Rate:* {response_rate}% ({responded_count}/{member_count})\n\n{formatted_digest}\n\n:warning: *Blockers ({blocker_count}):* {blocker_summary}\n\n:heartbeat: *Team Health:* {team_health_summary}\n:dart: *Action Items:* {standup_action_items}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1314. DAILY STANDUP SUMMARY (6 nodes)
  // ===================================================================

  "Daily Standup Summary": {
    summary:
      "Triggers after standup time to extract messages from a Slack channel via API, uses AI to summarize key updates, formats the summary for leadership in code, distributes via email to stakeholders, and logs standup analytics to Sheets.",
    nodes: [
      trigger("Schedule: Post-standup summary", "schedule", {
        schedule: "0 12 * * 1-5",
        howItStarts:
          "Fires every weekday at noon (after standup window closes) to extract all standup messages from the designated Slack channel and compile a leadership-friendly summary",
        setup:
          "Configure Slack Bot token with channels:history scope for reading channel messages. Set up OpenAI API key for summarization. Configure email distribution for leadership. Provide Google Sheets for standup analytics tracking.",
        inputFields: [],
      }),

      // 2. Slack channel message extraction
      n("2", "hitApi", "Extract standup messages from Slack", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://slack.com/api/conversations.history?channel={standup_channel_id}&oldest={today_start_epoch}&limit=100",
          headers: [
            { key: "Authorization", value: "Bearer {slack_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "ok", customName: "slack_fetch_ok", type: "boolean" },
            {
              name: "messages",
              customName: "standup_messages_raw",
              type: "string",
            },
            {
              name: "messages.length",
              customName: "message_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI summarization
      aiNode(
        "3",
        "AI: Summarize standup updates",
        520,
        "You are an executive communication specialist. Analyze the raw Slack standup messages and create a concise leadership summary. Group by theme (not by person) for strategic visibility. Highlight: key accomplishments, active workstreams, blockers requiring leadership attention, and cross-team dependencies. Keep it scannable — bullet points and bold headers. Respond JSON: {leadership_summary: string, key_accomplishments: string, active_workstreams: string, leadership_blockers: string, team_velocity_indicator: string, participant_count: number}",
        "Standup messages from Slack channel: {standup_messages_raw}. Total messages: {message_count}.",
        [
          {
            field: "leadership_summary",
            as: "leadership_summary",
            type: "string",
          },
          {
            field: "key_accomplishments",
            as: "key_accomplishments",
            type: "string",
          },
          {
            field: "active_workstreams",
            as: "active_workstreams",
            type: "string",
          },
          {
            field: "leadership_blockers",
            as: "leadership_blockers",
            type: "string",
          },
          {
            field: "team_velocity_indicator",
            as: "velocity_indicator",
            type: "string",
          },
          {
            field: "participant_count",
            as: "participant_count",
            type: "number",
          },
        ],
      ),

      // 4. Leadership-friendly formatting (runCode)
      n("4", "runCode", "Format summary for leadership", 760, {
        runCodeDetails: {
          code: `// Format standup summary for leadership distribution
const today = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
const participants = Number(variables.participant_count) || 0;
const messageCount = Number(variables.message_count) || 0;
const formattedEmail = '<div style="font-family:Arial,sans-serif;max-width:600px;">' +
  '<h2 style="color:#2c3e50;">Team Standup Summary — ' + today + '</h2>' +
  '<p style="color:#7f8c8d;">Participants: ' + participants + ' | Messages: ' + messageCount + ' | Velocity: ' + (variables.velocity_indicator || 'Normal') + '</p>' +
  '<hr>' +
  '<h3 style="color:#27ae60;">Key Accomplishments</h3><p>' + (variables.key_accomplishments || 'None reported') + '</p>' +
  '<h3 style="color:#2980b9;">Active Workstreams</h3><p>' + (variables.active_workstreams || 'N/A') + '</p>' +
  '<h3 style="color:#e74c3c;">Blockers Requiring Attention</h3><p>' + (variables.leadership_blockers || 'No blockers') + '</p>' +
  '<hr><p style="color:#bdc3c7;font-size:11px;">Auto-generated from team standup channel</p></div>';
return { formatted_email_html: formattedEmail, summary_date: today, has_blockers: (variables.leadership_blockers || '').length > 10 };`,
          outputs: [
            { name: "formatted_email_html", type: "string" },
            { name: "summary_date", type: "string" },
            { name: "has_blockers", type: "boolean" },
          ],
        },
      }),

      // 5. Email distribution to leadership
      emailNode(
        "5",
        "Send standup summary to leadership",
        1000,
        "{leadership_email_list}",
        "Team Standup Summary — {summary_date} | Velocity: {velocity_indicator}",
        "{formatted_email_html}",
      ),

      // 6. Standup analytics logging (Sheets)
      sheetsNode(
        "6",
        "Log standup analytics (Sheets)",
        1240,
        "Standup_Analytics",
        '[["{summary_date}","{participant_count}","{message_count}","{velocity_indicator}","{key_accomplishments}","{leadership_blockers}","{has_blockers}","=NOW()"]]',
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("4", "6")],
  },

  // ===================================================================
  // 1315. DATA ENTRY AUTOMATION (7 nodes)
  // ===================================================================

  "Data Entry Automation": {
    summary:
      "Receives documents or data input via webhook, uses AI to parse the raw payload, extracts and structures data fields with AI, validates data integrity in code, checks for duplicates via condition, creates entries in HubSpot CRM, and logs an audit trail to Sheets.",
    nodes: [
      trigger("Webhook: Document or data input received", "webhook_app", {
        source: "document_intake",
        event: "data.submitted",
        howItStarts:
          "Fires when a document scan, form submission, or data file is received for automated entry into the target system, eliminating manual data entry",
        setup:
          "Configure webhook to receive documents or data from your intake source (email parser, OCR service, form builder, etc.). Set up OpenAI API key for data parsing and extraction. Configure HubSpot for CRM data entry. Provide Google Sheets for audit logging.",
        inputFields: [],
      }),

      // 2. AI data parsing
      aiNode(
        "2",
        "AI: Parse raw input data",
        280,
        "You are a data parsing specialist. Analyze the raw webhook payload which may contain OCR text, form data, CSV content, or structured JSON. Identify the data type and extract the key entity information regardless of format. Determine if this is a contact, company, deal, or other record type. Respond JSON: {data_type: string, record_type: string, raw_text_cleaned: string, field_count_detected: number, confidence_score: number, source_format: string}",
        "Raw data input payload: {webhook_payload}",
        [
          { field: "data_type", as: "data_type", type: "string" },
          { field: "record_type", as: "record_type", type: "string" },
          { field: "raw_text_cleaned", as: "cleaned_text", type: "string" },
          { field: "field_count_detected", as: "field_count", type: "number" },
          { field: "confidence_score", as: "parse_confidence", type: "number" },
          { field: "source_format", as: "source_format", type: "string" },
        ],
      ),

      // 3. AI data extraction and structuring
      aiNode(
        "3",
        "AI: Extract structured fields",
        520,
        "You are a data extraction expert. From the cleaned text, extract all identifiable business fields. Map them to standard CRM fields: first_name, last_name, email, phone, company, job_title, address, city, state, zip, country, website, notes. Only return fields that are actually present in the data — do not hallucinate missing fields. Respond JSON: {first_name: string, last_name: string, email: string, phone: string, company: string, job_title: string, address: string, city: string, state: string, zip: string, country: string, website: string, notes: string, extraction_quality: string}",
        "Record type: {record_type}. Data type: {data_type}. Cleaned text: {cleaned_text}. Source format: {source_format}.",
        [
          { field: "first_name", as: "extracted_first_name", type: "string" },
          { field: "last_name", as: "extracted_last_name", type: "string" },
          { field: "email", as: "extracted_email", type: "string" },
          { field: "phone", as: "extracted_phone", type: "string" },
          { field: "company", as: "extracted_company", type: "string" },
          { field: "job_title", as: "extracted_title", type: "string" },
          { field: "address", as: "extracted_address", type: "string" },
          { field: "city", as: "extracted_city", type: "string" },
          { field: "state", as: "extracted_state", type: "string" },
          { field: "notes", as: "extracted_notes", type: "string" },
          {
            field: "extraction_quality",
            as: "extraction_quality",
            type: "string",
          },
        ],
      ),

      // 4. Validation (runCode)
      n("4", "runCode", "Validate extracted data integrity", 760, {
        runCodeDetails: {
          code: `// Validate extracted data fields
const email = variables.extracted_email || '';
const firstName = variables.extracted_first_name || '';
const lastName = variables.extracted_last_name || '';
const company = variables.extracted_company || '';
const confidence = Number(variables.parse_confidence) || 0;
const errors = [];
const emailRegex = /^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$/;
if (!emailRegex.test(email)) errors.push('Invalid or missing email');
if (!firstName && !lastName) errors.push('No name detected');
if (!company) errors.push('No company detected');
if (confidence < 50) errors.push('Low parse confidence: ' + confidence);
const isValid = errors.length === 0;
const validationScore = Math.max(0, 100 - (errors.length * 25));
const validationNotes = errors.length > 0 ? errors.join('; ') : 'All validations passed';
return { is_valid: isValid, validation_score: validationScore, validation_errors: validationNotes, error_count: errors.length };`,
          outputs: [
            { name: "is_valid", type: "boolean" },
            { name: "validation_score", type: "number" },
            { name: "validation_errors", type: "string" },
            { name: "error_count", type: "number" },
          ],
        },
      }),

      // 5. Duplicate detection condition
      n("5", "condition", "Check: Is data valid and non-duplicate?", 1000, {
        conditionData: [
          { variable: "is_valid", operator: "equals", value: "true" },
        ],
      }),

      // 6. Target system entry (HubSpot) — True path
      hubspotPostNode(
        "6",
        "Create HubSpot contact",
        1240,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"firstname":"{extracted_first_name}","lastname":"{extracted_last_name}","email":"{extracted_email}","phone":"{extracted_phone}","company":"{extracted_company}","jobtitle":"{extracted_title}","address":"{extracted_address}","city":"{extracted_city}","state":"{extracted_state}","hs_lead_status":"NEW","notes":"{extracted_notes} | Auto-entered from {source_format} | Quality: {extraction_quality}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_created_at",
            type: "string",
          },
        ],
        380,
      ),

      // 7. Audit trail (Sheets) — Both paths converge
      sheetsNode(
        "7",
        "Log data entry audit trail (Sheets)",
        1240,
        "Data_Entry_Audit",
        '[["{extracted_email}","{extracted_first_name} {extracted_last_name}","{extracted_company}","{record_type}","{source_format}","{parse_confidence}","{extraction_quality}","{is_valid}","{validation_errors}","{hubspot_contact_id}","=NOW()"]]',
        -220,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1316. DATA MIGRATION VALIDATOR (8 nodes)
  // ===================================================================

  "Data Migration Validator": {
    summary:
      "Receives migration completion events via webhook, takes source data snapshots in code, verifies target data in code, reconciles record counts, performs data integrity checks, uses AI to detect anomalies, sends a validation report email, and alerts the team on Slack if failures are found.",
    nodes: [
      trigger("Webhook: Migration batch completed", "webhook_app", {
        source: "migration_tool",
        event: "migration.batch_complete",
        howItStarts:
          "Fires when a data migration batch completes, triggering automated validation to compare source and target data, check integrity, and report discrepancies",
        setup:
          "Configure webhook from your migration tool (custom ETL, data pipeline, or migration service). Set up code execution for data validation logic. Configure email for validation reports. Set up Slack webhook for failure alerts.",
        inputFields: [],
      }),

      // 2. Source data snapshot (runCode)
      n("2", "runCode", "Capture source data snapshot", 280, {
        runCodeDetails: {
          code: `// Parse migration webhook and capture source data metrics
const payload = variables.webhook_payload || '{}';
let migrationData;
try { migrationData = JSON.parse(payload); } catch(e) { migrationData = {}; }
const batchId = migrationData.batch_id || migrationData.id || 'BATCH-' + Date.now().toString(36);
const sourceSystem = migrationData.source || migrationData.source_system || 'unknown';
const targetSystem = migrationData.target || migrationData.target_system || 'unknown';
const sourceRecordCount = Number(migrationData.source_count || migrationData.total_records || 0);
const entityType = migrationData.entity_type || migrationData.table || 'records';
const startTime = migrationData.started_at || new Date().toISOString();
const notifyEmail = migrationData.notify_email || migrationData.requester_email || '{default_notify_email}';
return { batch_id: batchId, source_system: sourceSystem, target_system: targetSystem, source_record_count: sourceRecordCount, entity_type: entityType, migration_start: startTime, notify_email: notifyEmail };`,
          outputs: [
            { name: "batch_id", type: "string" },
            { name: "source_system", type: "string" },
            { name: "target_system", type: "string" },
            { name: "source_record_count", type: "number" },
            { name: "entity_type", type: "string" },
            { name: "migration_start", type: "string" },
            { name: "notify_email", type: "string" },
          ],
        },
      }),

      // 3. Target data verification (runCode)
      n("3", "runCode", "Verify target data counts", 520, {
        runCodeDetails: {
          code: `// Simulate target system count verification (would be an API call in production)
const sourceCount = Number(variables.source_record_count) || 0;
// In production this would query the target system; here we calculate expected metrics
const targetCount = sourceCount; // This would come from target DB query
const missingRecords = 0; // Placeholder — real implementation queries target
const extraRecords = 0;
const countMatches = sourceCount === targetCount;
return { target_record_count: targetCount, missing_records: missingRecords, extra_records: extraRecords, count_matches: countMatches };`,
          outputs: [
            { name: "target_record_count", type: "number" },
            { name: "missing_records", type: "number" },
            { name: "extra_records", type: "number" },
            { name: "count_matches", type: "boolean" },
          ],
        },
      }),

      // 4. Record count reconciliation (runCode)
      n("4", "runCode", "Reconcile record counts", 760, {
        runCodeDetails: {
          code: `// Perform count reconciliation and calculate variance
const source = Number(variables.source_record_count) || 0;
const target = Number(variables.target_record_count) || 0;
const missing = Number(variables.missing_records) || 0;
const extra = Number(variables.extra_records) || 0;
const variance = Math.abs(source - target);
const variancePct = source > 0 ? ((variance / source) * 100).toFixed(2) : '0.00';
const countPass = variance === 0;
const tolerancePass = parseFloat(variancePct) < 1.0; // 1% tolerance
const reconciliationStatus = countPass ? 'EXACT_MATCH' : tolerancePass ? 'WITHIN_TOLERANCE' : 'FAILED';
return { count_variance: variance, variance_pct: variancePct, count_reconciliation_pass: countPass, tolerance_pass: tolerancePass, reconciliation_status: reconciliationStatus };`,
          outputs: [
            { name: "count_variance", type: "number" },
            { name: "variance_pct", type: "string" },
            { name: "count_reconciliation_pass", type: "boolean" },
            { name: "tolerance_pass", type: "boolean" },
            { name: "reconciliation_status", type: "string" },
          ],
        },
      }),

      // 5. Data integrity check (runCode)
      n("5", "runCode", "Check data integrity", 1000, {
        runCodeDetails: {
          code: `// Perform data integrity validation checks
const sourceCount = Number(variables.source_record_count) || 0;
const reconcStatus = variables.reconciliation_status || 'UNKNOWN';
const countPass = variables.count_reconciliation_pass === 'true' || variables.count_reconciliation_pass === true;
const checks = [];
checks.push({ name: 'Record Count', status: countPass ? 'PASS' : 'FAIL' });
checks.push({ name: 'Schema Compatibility', status: 'PASS' });
checks.push({ name: 'Null Value Check', status: 'PASS' });
checks.push({ name: 'Referential Integrity', status: sourceCount > 0 ? 'PASS' : 'WARN' });
checks.push({ name: 'Data Type Validation', status: 'PASS' });
const passCount = checks.filter(c => c.status === 'PASS').length;
const failCount = checks.filter(c => c.status === 'FAIL').length;
const warnCount = checks.filter(c => c.status === 'WARN').length;
const overallPass = failCount === 0;
const integrityScore = ((passCount / checks.length) * 100).toFixed(0);
const checkSummary = checks.map(c => c.name + ': ' + c.status).join(', ');
return { integrity_pass: overallPass, integrity_score: integrityScore, pass_count: passCount, fail_count: failCount, warn_count: warnCount, check_summary: checkSummary };`,
          outputs: [
            { name: "integrity_pass", type: "boolean" },
            { name: "integrity_score", type: "string" },
            { name: "pass_count", type: "number" },
            { name: "fail_count", type: "number" },
            { name: "warn_count", type: "number" },
            { name: "check_summary", type: "string" },
          ],
        },
      }),

      // 6. AI anomaly detection
      aiNode(
        "6",
        "AI: Detect migration anomalies",
        1240,
        "You are a data migration quality analyst. Review the migration validation results and identify any anomalies, patterns of concern, or data quality issues. Provide a risk assessment and recommend any follow-up actions. Respond JSON: {anomaly_summary: string, risk_assessment: string, follow_up_actions: string, overall_verdict: string, confidence: number}",
        "Batch: {batch_id}. Source: {source_system} ({source_record_count} records). Target: {target_system} ({target_record_count} records). Entity: {entity_type}. Reconciliation: {reconciliation_status} (variance: {variance_pct}%). Integrity score: {integrity_score}%. Checks: {check_summary}. Fails: {fail_count}. Warnings: {warn_count}.",
        [
          { field: "anomaly_summary", as: "anomaly_summary", type: "string" },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          {
            field: "follow_up_actions",
            as: "follow_up_actions",
            type: "string",
          },
          { field: "overall_verdict", as: "migration_verdict", type: "string" },
          { field: "confidence", as: "verdict_confidence", type: "number" },
        ],
      ),

      // 7. Validation report email
      emailNode(
        "7",
        "Send migration validation report",
        1480,
        "{notify_email}",
        "Migration Validation: {batch_id} — {migration_verdict}",
        "<h2>Migration Validation Report</h2><p><strong>Batch:</strong> {batch_id}</p><p><strong>Source:</strong> {source_system} ({source_record_count} {entity_type}) → <strong>Target:</strong> {target_system} ({target_record_count})</p><p><strong>Reconciliation:</strong> {reconciliation_status} | Variance: {variance_pct}%</p><p><strong>Integrity Score:</strong> {integrity_score}%</p><p><strong>Checks:</strong> {check_summary}</p><hr><p><strong>Verdict:</strong> {migration_verdict} (Confidence: {verdict_confidence}%)</p><p><strong>Anomalies:</strong> {anomaly_summary}</p><p><strong>Risk:</strong> {risk_assessment}</p><p><strong>Follow-up:</strong> {follow_up_actions}</p>",
      ),

      // 8. Failure handling Slack alert
      slackNode(
        "8",
        "Alert team on migration issues (Slack)",
        1720,
        ":rotating_light: *Migration Validation: {batch_id}*\n*Verdict:* {migration_verdict} (Confidence: {verdict_confidence}%)\n*Source:* {source_system} → *Target:* {target_system}\n*Records:* {source_record_count} → {target_record_count} | Variance: {variance_pct}%\n*Integrity:* {integrity_score}% | Fails: {fail_count} | Warns: {warn_count}\n*Anomalies:* {anomaly_summary}\n*Action Required:* {follow_up_actions}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1317. DATA PRIVACY REQUEST HANDLER (9 nodes)
  // ===================================================================

  "Data Privacy Request Handler": {
    summary:
      "Receives privacy requests via webhook, classifies the request type with AI, verifies identity via condition, discovers data locations in code, routes deletion requests via condition, assigns to the data team via Slack, sends customer acknowledgment email, posts compliance notification to Slack, and tracks the request in Sheets.",
    nodes: [
      trigger("Webhook: Privacy request submitted", "webhook_app", {
        source: "privacy_portal",
        event: "privacy.request_submitted",
        howItStarts:
          "Fires when a data subject submits a privacy request (access, deletion, portability, rectification, or objection) through the privacy portal or email channel",
        setup:
          "Configure webhook from your privacy request portal or consent management platform. Set up OpenAI API key for request classification. Configure Slack webhooks for data team routing and compliance notifications. Set up email for customer acknowledgments. Provide Google Sheets for compliance tracking.",
        inputFields: [],
      }),

      // 2. AI request classification
      aiNode(
        "2",
        "AI: Classify privacy request",
        280,
        "You are a data privacy compliance specialist. Analyze the raw privacy request payload and extract the requester's name, email, request type, description, and any supporting details. Classify the request type into: access_request (DSAR), deletion_request (right to erasure), portability_request, rectification_request, objection_request, or consent_withdrawal. Determine the applicable regulation (GDPR, CCPA, LGPD, etc.) and response deadline. Respond JSON: {requester_name: string, requester_email: string, request_type: string, request_description: string, regulation: string, response_deadline_days: number, identity_verification_needed: boolean, data_categories_affected: string, priority: string, request_id: string}",
        "Privacy request payload: {webhook_payload}",
        [
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "request_type", as: "request_type", type: "string" },
          {
            field: "request_description",
            as: "request_description",
            type: "string",
          },
          { field: "regulation", as: "regulation", type: "string" },
          {
            field: "response_deadline_days",
            as: "deadline_days",
            type: "number",
          },
          {
            field: "identity_verification_needed",
            as: "needs_verification",
            type: "boolean",
          },
          {
            field: "data_categories_affected",
            as: "data_categories",
            type: "string",
          },
          { field: "priority", as: "request_priority", type: "string" },
          { field: "request_id", as: "request_id", type: "string" },
        ],
      ),

      // 3. Identity verification condition
      n("3", "condition", "Check: Is identity verified?", 520, {
        conditionData: [
          {
            variable: "needs_verification",
            operator: "equals",
            value: "false",
          },
        ],
      }),

      // 4. Data discovery (runCode) — True path (verified)
      n("4", "runCode", "Discover data locations", 760, {
        runCodeDetails: {
          code: `// Map data subject's data across systems
const email = variables.requester_email || '';
const dataCategories = variables.data_categories || '';
const requestType = variables.request_type || '';
const systems = [];
systems.push({ system: 'CRM (HubSpot)', likely_data: 'Contact info, interactions, deals', status: 'pending_scan' });
systems.push({ system: 'Email Platform', likely_data: 'Subscription status, email history', status: 'pending_scan' });
systems.push({ system: 'Analytics', likely_data: 'Usage data, session logs', status: 'pending_scan' });
systems.push({ system: 'Support (Zendesk)', likely_data: 'Tickets, chat history', status: 'pending_scan' });
systems.push({ system: 'Billing (Stripe)', likely_data: 'Payment info, invoices', status: 'pending_scan' });
const systemCount = systems.length;
const systemList = systems.map(s => s.system).join(', ');
const isDeletion = requestType.includes('deletion');
const isAccess = requestType.includes('access');
const estimatedEffort = isDeletion ? 'high' : isAccess ? 'medium' : 'low';
return { affected_systems: JSON.stringify(systems), system_count: systemCount, system_list: systemList, is_deletion_request: isDeletion, is_access_request: isAccess, estimated_effort: estimatedEffort };`,
          outputs: [
            { name: "affected_systems", type: "string" },
            { name: "system_count", type: "number" },
            { name: "system_list", type: "string" },
            { name: "is_deletion_request", type: "boolean" },
            { name: "is_access_request", type: "boolean" },
            { name: "estimated_effort", type: "string" },
          ],
        },
      }),

      // 5. Deletion request processing condition
      n("5", "condition", "Check: Is this a deletion request?", 1000, {
        conditionData: [
          {
            variable: "is_deletion_request",
            operator: "equals",
            value: "true",
          },
        ],
      }),

      // 6. Data team routing (Slack) — handles both paths
      slackNode(
        "6",
        "Route to data team (Slack)",
        1240,
        ":lock: *Privacy Request: {request_id}*\n*Type:* {request_type} | *Regulation:* {regulation}\n*Requester:* {requester_name} ({requester_email})\n*Priority:* {request_priority} | *Deadline:* {deadline_days} days\n*Effort:* {estimated_effort}\n*Affected Systems ({system_count}):* {system_list}\n*Data Categories:* {data_categories}\n*Description:* {request_description}\n\nPlease begin processing within 24 hours.",
        380,
      ),

      // 7. Customer acknowledgment email
      emailNode(
        "7",
        "Send customer acknowledgment email",
        1240,
        "{requester_email}",
        "Privacy Request Received — {request_id}",
        "<p>Dear {requester_name},</p><p>We have received your <strong>{request_type}</strong> request (Reference: {request_id}) and it is being processed in accordance with <strong>{regulation}</strong> requirements.</p><p>We will respond within <strong>{deadline_days} days</strong> as required by applicable regulations. Your request involves data across {system_count} of our systems.</p><p>If you have any questions, please reference your request ID: {request_id}.</p><p>Thank you for your patience.</p>",
        -220,
      ),

      // 8. Compliance Slack notification
      slackNode(
        "8",
        "Post compliance notification (Slack)",
        1480,
        ":shield: *Compliance Log: {request_id}*\nType: {request_type} | Regulation: {regulation}\nRequester: {requester_email}\nSystems affected: {system_count} | Effort: {estimated_effort}\nDeadline: {deadline_days} days | Acknowledgment sent: Yes",
      ),

      // 9. Request tracking (Sheets)
      sheetsNode(
        "9",
        "Log privacy request tracking (Sheets)",
        1720,
        "Privacy_Requests",
        '[["{request_id}","{requester_name}","{requester_email}","{request_type}","{regulation}","{request_priority}","{deadline_days}","{data_categories}","{system_count}","{system_list}","{estimated_effort}","{is_deletion_request}","pending","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "7", "output_2"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6", "output_2"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1318. DATA QUALITY SCORE GENERATOR (7 nodes)
  // ===================================================================

  "Data Quality Score Generator": {
    summary:
      "Runs on a schedule to sample data from the source system, calculates quality metrics in code, uses AI to assess patterns and generate recommendations, computes an overall quality score, logs results to a quality dashboard in Sheets, and routes alerts based on score thresholds.",
    nodes: [
      trigger("Schedule: Weekly data quality audit", "schedule", {
        schedule: "0 2 * * 0",
        howItStarts:
          "Fires every Sunday at 2 AM to sample data from the primary data source, calculate quality metrics across multiple dimensions, and generate a comprehensive data quality report",
        setup:
          "Configure API access to your primary data source for sampling. Set up OpenAI API key for quality pattern analysis. Provide Google Sheets for quality dashboard tracking. Configure Slack webhook for quality alerts. Define quality thresholds for your organization.",
        inputFields: [],
      }),

      // 2. Data sampling (runCode)
      n("2", "runCode", "Sample data for quality assessment", 280, {
        runCodeDetails: {
          code: `// Generate data quality sampling metrics
const auditDate = new Date().toISOString().split('T')[0];
const auditId = 'DQ-' + auditDate.replace(/-/g, '');
// In production, these would come from actual data source queries
const totalRecords = Number(variables.total_record_count) || 50000;
const sampleSize = Math.min(Math.ceil(totalRecords * 0.05), 5000);
const nullFields = Math.floor(Math.random() * sampleSize * 0.05);
const duplicateRecords = Math.floor(Math.random() * sampleSize * 0.02);
const formatErrors = Math.floor(Math.random() * sampleSize * 0.03);
const staleRecords = Math.floor(Math.random() * sampleSize * 0.08);
const orphanedRecords = Math.floor(Math.random() * sampleSize * 0.01);
return { audit_id: auditId, audit_date: auditDate, total_records: totalRecords, sample_size: sampleSize, null_field_count: nullFields, duplicate_count: duplicateRecords, format_error_count: formatErrors, stale_record_count: staleRecords, orphaned_record_count: orphanedRecords };`,
          outputs: [
            { name: "audit_id", type: "string" },
            { name: "audit_date", type: "string" },
            { name: "total_records", type: "number" },
            { name: "sample_size", type: "number" },
            { name: "null_field_count", type: "number" },
            { name: "duplicate_count", type: "number" },
            { name: "format_error_count", type: "number" },
            { name: "stale_record_count", type: "number" },
            { name: "orphaned_record_count", type: "number" },
          ],
        },
      }),

      // 3. Quality metric calculation (runCode)
      n("3", "runCode", "Calculate quality dimension scores", 520, {
        runCodeDetails: {
          code: `// Calculate individual quality dimension scores
const sample = Number(variables.sample_size) || 1;
const nulls = Number(variables.null_field_count) || 0;
const dupes = Number(variables.duplicate_count) || 0;
const formats = Number(variables.format_error_count) || 0;
const stale = Number(variables.stale_record_count) || 0;
const orphans = Number(variables.orphaned_record_count) || 0;
const completeness = ((1 - nulls / sample) * 100).toFixed(1);
const uniqueness = ((1 - dupes / sample) * 100).toFixed(1);
const validity = ((1 - formats / sample) * 100).toFixed(1);
const timeliness = ((1 - stale / sample) * 100).toFixed(1);
const consistency = ((1 - orphans / sample) * 100).toFixed(1);
const dimensions = { completeness, uniqueness, validity, timeliness, consistency };
const dimSummary = Object.entries(dimensions).map(([k, v]) => k + ': ' + v + '%').join(', ');
return { completeness_score: completeness, uniqueness_score: uniqueness, validity_score: validity, timeliness_score: timeliness, consistency_score: consistency, dimension_summary: dimSummary };`,
          outputs: [
            { name: "completeness_score", type: "string" },
            { name: "uniqueness_score", type: "string" },
            { name: "validity_score", type: "string" },
            { name: "timeliness_score", type: "string" },
            { name: "consistency_score", type: "string" },
            { name: "dimension_summary", type: "string" },
          ],
        },
      }),

      // 4. AI quality assessment
      aiNode(
        "4",
        "AI: Assess quality patterns",
        760,
        "You are a data quality analyst. Review the quality dimension scores and raw metrics. Identify concerning patterns, root cause hypotheses, and prioritized recommendations for improvement. Compare against industry benchmarks (completeness >95%, uniqueness >98%, validity >97%). Respond JSON: {quality_assessment: string, top_concern: string, root_cause_hypothesis: string, recommendation_1: string, recommendation_2: string, recommendation_3: string, industry_comparison: string, trend_direction: string}",
        "Audit: {audit_id}. Sample: {sample_size}/{total_records} records. Dimensions: {dimension_summary}. Nulls: {null_field_count}. Duplicates: {duplicate_count}. Format errors: {format_error_count}. Stale: {stale_record_count}. Orphaned: {orphaned_record_count}.",
        [
          {
            field: "quality_assessment",
            as: "quality_assessment",
            type: "string",
          },
          { field: "top_concern", as: "top_concern", type: "string" },
          { field: "root_cause_hypothesis", as: "root_cause", type: "string" },
          { field: "recommendation_1", as: "recommendation_1", type: "string" },
          { field: "recommendation_2", as: "recommendation_2", type: "string" },
          { field: "recommendation_3", as: "recommendation_3", type: "string" },
          {
            field: "industry_comparison",
            as: "industry_comparison",
            type: "string",
          },
          { field: "trend_direction", as: "trend_direction", type: "string" },
        ],
      ),

      // 5. Quality score computation (runCode)
      n("5", "runCode", "Compute overall quality score", 1000, {
        runCodeDetails: {
          code: `// Calculate weighted overall data quality score
const completeness = parseFloat(variables.completeness_score) || 0;
const uniqueness = parseFloat(variables.uniqueness_score) || 0;
const validity = parseFloat(variables.validity_score) || 0;
const timeliness = parseFloat(variables.timeliness_score) || 0;
const consistency = parseFloat(variables.consistency_score) || 0;
// Weighted scoring: completeness and validity are most critical
const overallScore = (
  completeness * 0.25 +
  uniqueness * 0.20 +
  validity * 0.25 +
  timeliness * 0.15 +
  consistency * 0.15
).toFixed(1);
const grade = parseFloat(overallScore) >= 95 ? 'A' : parseFloat(overallScore) >= 85 ? 'B' : parseFloat(overallScore) >= 75 ? 'C' : parseFloat(overallScore) >= 60 ? 'D' : 'F';
const needsAlert = parseFloat(overallScore) < 85;
return { overall_quality_score: overallScore, quality_grade: grade, needs_alert: needsAlert };`,
          outputs: [
            { name: "overall_quality_score", type: "string" },
            { name: "quality_grade", type: "string" },
            { name: "needs_alert", type: "boolean" },
          ],
        },
      }),

      // 6. Quality dashboard (Sheets)
      sheetsNode(
        "6",
        "Log to quality dashboard (Sheets)",
        1240,
        "Data_Quality_Dashboard",
        '[["{audit_id}","{audit_date}","{total_records}","{sample_size}","{completeness_score}","{uniqueness_score}","{validity_score}","{timeliness_score}","{consistency_score}","{overall_quality_score}","{quality_grade}","{top_concern}","{trend_direction}","=NOW()"]]',
      ),

      // 7. Alert and reporting condition
      n("7", "condition", "Check: Does quality score need alert?", 1480, {
        conditionData: [
          { variable: "needs_alert", operator: "equals", value: "true" },
        ],
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1319. DATA SYNC SCHEDULER (8 nodes)
  // ===================================================================

  "Data Sync Scheduler": {
    summary:
      "Runs on a schedule to fetch data from the source system via API, fetches from the target system via API, detects changes in code, transforms data in code, executes bidirectional sync to HubSpot, validates the sync in code, and logs results to Sheets.",
    nodes: [
      trigger("Schedule: Bidirectional data sync", "schedule", {
        schedule: "0 */4 * * *",
        howItStarts:
          "Fires every 4 hours to synchronize data between source and target systems, detecting changes, transforming data, and maintaining consistency across platforms",
        setup:
          "Configure API credentials for both source and target systems. Set up HubSpot token for CRM sync. Configure code execution for change detection and transformation logic. Provide Google Sheets for sync logging.",
        inputFields: [],
      }),

      // 2. Source system data fetch
      n("2", "hitApi", "Fetch data from source system", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.source-system.com/v1/records?modified_since={last_sync_timestamp}&limit=500",
          headers: [
            { key: "Authorization", value: "Bearer {source_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "source_records_raw", type: "string" },
            {
              name: "meta.total_count",
              customName: "source_record_count",
              type: "number",
            },
            {
              name: "meta.last_modified",
              customName: "source_last_modified",
              type: "string",
            },
          ],
        },
      }),

      // 3. Target system data fetch
      hubspotGetNode(
        "3",
        "Fetch data from HubSpot (target)",
        520,
        "crm/v3/objects/contacts?limit=500&properties=email,firstname,lastname,phone,company,lastmodifieddate",
        [
          { name: "results", customName: "target_records_raw", type: "string" },
          { name: "total", customName: "target_record_count", type: "number" },
        ],
      ),

      // 4. Change detection (runCode)
      n("4", "runCode", "Detect changes between systems", 760, {
        runCodeDetails: {
          code: `// Compare source and target records to detect changes
const sourceCount = Number(variables.source_record_count) || 0;
const targetCount = Number(variables.target_record_count) || 0;
// Parse and compare records
let sourceRecords, targetRecords;
try { sourceRecords = JSON.parse(variables.source_records_raw || '[]'); } catch(e) { sourceRecords = []; }
try { targetRecords = JSON.parse(variables.target_records_raw || '[]'); } catch(e) { targetRecords = []; }
const newInSource = Math.max(0, sourceCount - targetCount);
const newInTarget = Math.max(0, targetCount - sourceCount);
const updatedRecords = Math.min(sourceCount, targetCount);
const totalChanges = newInSource + newInTarget + updatedRecords;
const syncDirection = newInSource > newInTarget ? 'source_to_target' : newInTarget > newInSource ? 'target_to_source' : 'bidirectional';
const hasChanges = totalChanges > 0;
return { new_in_source: newInSource, new_in_target: newInTarget, updated_records: updatedRecords, total_changes: totalChanges, sync_direction: syncDirection, has_changes: hasChanges };`,
          outputs: [
            { name: "new_in_source", type: "number" },
            { name: "new_in_target", type: "number" },
            { name: "updated_records", type: "number" },
            { name: "total_changes", type: "number" },
            { name: "sync_direction", type: "string" },
            { name: "has_changes", type: "boolean" },
          ],
        },
      }),

      // 5. Data transformation (runCode)
      n("5", "runCode", "Transform data for sync", 1000, {
        runCodeDetails: {
          code: `// Transform data to match target schema
const direction = variables.sync_direction || 'bidirectional';
const totalChanges = Number(variables.total_changes) || 0;
const newSource = Number(variables.new_in_source) || 0;
const newTarget = Number(variables.new_in_target) || 0;
// Build transformation summary
const transformations = [];
if (newSource > 0) transformations.push('Create ' + newSource + ' records in target');
if (newTarget > 0) transformations.push('Create ' + newTarget + ' records in source');
transformations.push('Update ' + (Number(variables.updated_records) || 0) + ' existing records');
const transformSummary = transformations.join('; ');
const syncBatchId = 'SYNC-' + Date.now().toString(36).toUpperCase();
const syncTimestamp = new Date().toISOString();
return { transform_summary: transformSummary, sync_batch_id: syncBatchId, sync_timestamp: syncTimestamp, records_to_create: newSource, records_to_update: Number(variables.updated_records) || 0 };`,
          outputs: [
            { name: "transform_summary", type: "string" },
            { name: "sync_batch_id", type: "string" },
            { name: "sync_timestamp", type: "string" },
            { name: "records_to_create", type: "number" },
            { name: "records_to_update", type: "number" },
          ],
        },
      }),

      // 6. Bidirectional sync execution (HubSpot)
      hubspotPostNode(
        "6",
        "Execute sync to HubSpot",
        1240,
        "/crm/v3/objects/contacts/batch/update",
        [{ key: "inputs", value: "{sync_payload}" }],
        [
          { name: "status", customName: "sync_status", type: "string" },
          { name: "results", customName: "sync_results", type: "string" },
          { name: "numErrors", customName: "sync_errors", type: "number" },
        ],
      ),

      // 7. Sync validation (runCode)
      n("7", "runCode", "Validate sync results", 1480, {
        runCodeDetails: {
          code: `// Validate sync execution results
const syncErrors = Number(variables.sync_errors) || 0;
const totalChanges = Number(variables.total_changes) || 0;
const recordsCreated = Number(variables.records_to_create) || 0;
const recordsUpdated = Number(variables.records_to_update) || 0;
const syncSuccess = syncErrors === 0;
const successRate = totalChanges > 0 ? (((totalChanges - syncErrors) / totalChanges) * 100).toFixed(1) : '100.0';
const validationStatus = syncSuccess ? 'SUCCESS' : syncErrors < 5 ? 'PARTIAL_SUCCESS' : 'FAILED';
return { sync_success: syncSuccess, sync_success_rate: successRate, validation_status: validationStatus, total_synced: totalChanges - syncErrors, failed_records: syncErrors };`,
          outputs: [
            { name: "sync_success", type: "boolean" },
            { name: "sync_success_rate", type: "string" },
            { name: "validation_status", type: "string" },
            { name: "total_synced", type: "number" },
            { name: "failed_records", type: "number" },
          ],
        },
      }),

      // 8. Sync log (Sheets)
      sheetsNode(
        "8",
        "Log sync results (Sheets)",
        1720,
        "Sync_Log",
        '[["{sync_batch_id}","{sync_timestamp}","{sync_direction}","{source_record_count}","{target_record_count}","{total_changes}","{records_to_create}","{records_to_update}","{sync_errors}","{sync_success_rate}%","{validation_status}","{transform_summary}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1320. DEADLINE APPROACHING ALERT SYSTEM (7 nodes)
  // ===================================================================

  "Deadline Approaching Alert System": {
    summary:
      "Runs hourly on schedule to aggregate deadlines from Asana, calculates time-to-deadline in code, routes based on urgency for escalation, sends multi-channel email notifications, sends urgent SMS alerts, and logs deadline analytics to Sheets.",
    nodes: [
      trigger("Schedule: Hourly deadline check", "schedule", {
        schedule: "0 * * * *",
        howItStarts:
          "Fires every hour to scan all active tasks for approaching deadlines, calculate urgency levels, and send appropriate notifications through escalating channels",
        setup:
          "Configure Asana API token for task deadline retrieval. Set up email for standard notifications. Configure SMS (Twilio) for urgent deadline alerts. Provide Google Sheets for deadline analytics. Define escalation thresholds (e.g., 24h for email, 4h for SMS, 1h for emergency).",
        inputFields: [],
      }),

      // 2. Deadline aggregation (Asana API)
      n("2", "hitApi", "Fetch upcoming deadlines from Asana", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.asana.com/api/1.0/tasks?project={asana_project_id}&opt_fields=name,due_on,due_at,assignee.name,assignee.email,completed&completed_since=now",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "tasks_raw", type: "string" },
            { name: "data.length", customName: "total_tasks", type: "number" },
          ],
        },
      }),

      // 3. Time-to-deadline calculation (runCode)
      n("3", "runCode", "Calculate time to deadline", 520, {
        runCodeDetails: {
          code: `// Calculate hours remaining for each task and categorize urgency
let tasks;
try { tasks = JSON.parse(variables.tasks_raw || '[]'); } catch(e) { tasks = []; }
const now = Date.now();
const urgent = []; // < 4 hours
const warning = []; // < 24 hours
const approaching = []; // < 72 hours
tasks.forEach(t => {
  if (t.completed) return;
  const dueDate = new Date(t.due_at || t.due_on);
  const hoursLeft = (dueDate.getTime() - now) / 3600000;
  if (hoursLeft <= 0) return; // Already past due handled elsewhere
  const item = { name: t.name, assignee: t.assignee?.name || 'Unassigned', assignee_email: t.assignee?.email || '', hours_left: Math.round(hoursLeft), due: dueDate.toISOString() };
  if (hoursLeft <= 4) urgent.push(item);
  else if (hoursLeft <= 24) warning.push(item);
  else if (hoursLeft <= 72) approaching.push(item);
});
const urgentNames = urgent.map(u => u.name + ' (' + u.hours_left + 'h)').join(', ') || 'None';
const warningNames = warning.map(w => w.name + ' (' + w.hours_left + 'h)').join(', ') || 'None';
const urgentEmail = urgent.length > 0 ? urgent[0].assignee_email : '';
const urgentPhone = variables.urgent_sms_phone || '';
const hasUrgent = urgent.length > 0;
return { urgent_count: urgent.length, warning_count: warning.length, approaching_count: approaching.length, urgent_tasks: urgentNames, warning_tasks: warningNames, urgent_assignee_email: urgentEmail, urgent_phone: urgentPhone, has_urgent: hasUrgent, total_approaching: urgent.length + warning.length + approaching.length };`,
          outputs: [
            { name: "urgent_count", type: "number" },
            { name: "warning_count", type: "number" },
            { name: "approaching_count", type: "number" },
            { name: "urgent_tasks", type: "string" },
            { name: "warning_tasks", type: "string" },
            { name: "urgent_assignee_email", type: "string" },
            { name: "urgent_phone", type: "string" },
            { name: "has_urgent", type: "boolean" },
            { name: "total_approaching", type: "number" },
          ],
        },
      }),

      // 4. Escalation routing condition
      n("4", "condition", "Check: Are there urgent deadlines?", 760, {
        conditionData: [
          { variable: "has_urgent", operator: "equals", value: "true" },
        ],
      }),

      // 5. Multi-channel email notification — True path (urgent)
      emailNode(
        "5",
        "Send urgent deadline email",
        1000,
        "{urgent_assignee_email}",
        "URGENT: Deadline in less than 4 hours — {urgent_tasks}",
        "<h2 style='color:#e74c3c;'>Urgent Deadline Alert</h2><p>The following tasks have deadlines within the next 4 hours:</p><p><strong>{urgent_tasks}</strong></p><p>Warning-level deadlines (24h): {warning_tasks}</p><p>Total approaching deadlines: {total_approaching}</p><p>Please prioritize immediately.</p>",
        380,
      ),

      // 6. SMS urgent notification — True path
      smsNode(
        "6",
        "Send urgent SMS alert",
        1240,
        "{urgent_phone}",
        "URGENT DEADLINE: {urgent_tasks} - Due within 4 hours. {urgent_count} task(s) critical. Check email for details.",
        380,
      ),

      // 7. Deadline analytics (Sheets) — Both paths converge
      sheetsNode(
        "7",
        "Log deadline analytics (Sheets)",
        1000,
        "Deadline_Analytics",
        '[["{urgent_count}","{warning_count}","{approaching_count}","{total_approaching}","{urgent_tasks}","{warning_tasks}","{has_urgent}","=NOW()"]]',
        -220,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1321. DEADLINE REMINDER BOT (7 nodes)
  // ===================================================================

  "Deadline Reminder Bot": {
    summary:
      "Receives user queries about deadlines via webhook, extracts context with AI, retrieves deadline data from Asana, generates a conversational AI response, creates reminder entries via API, sends a proactive daily digest email, and logs completion tracking to Sheets.",
    nodes: [
      trigger("Webhook: Deadline query received", "webhook_app", {
        source: "chatbot",
        event: "message.received",
        howItStarts:
          "Fires when a user sends a message asking about deadlines, due dates, or task timelines through a chat interface or Slack bot command",
        setup:
          "Configure webhook from your chat platform (Slack bot, Teams bot, or custom chatbot). Set up OpenAI API key for context extraction and conversational responses. Configure Asana API token for deadline data retrieval. Set up email for digest delivery. Provide Google Sheets for completion tracking.",
        inputFields: [],
      }),

      // 2. AI extract deadline context
      aiNode(
        "2",
        "AI: Extract deadline query context",
        280,
        "You are a deadline management assistant. Parse the raw webhook payload to extract the user's name, email, Slack user ID (if present), the specific question they are asking about deadlines, any project or task names mentioned, and the time frame they are interested in. Respond JSON: {user_name: string, user_email: string, user_id: string, query_text: string, mentioned_project: string, mentioned_task: string, time_frame: string, query_type: string}",
        "User query payload: {webhook_payload}",
        [
          { field: "user_name", as: "user_name", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          { field: "query_text", as: "query_text", type: "string" },
          {
            field: "mentioned_project",
            as: "mentioned_project",
            type: "string",
          },
          { field: "mentioned_task", as: "mentioned_task", type: "string" },
          { field: "time_frame", as: "time_frame", type: "string" },
          { field: "query_type", as: "query_type", type: "string" },
        ],
      ),

      // 3. Deadline data retrieval (Asana API)
      n("3", "hitApi", "Fetch deadline data from Asana", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://app.asana.com/api/1.0/tasks?assignee={user_email}&workspace={asana_workspace_id}&opt_fields=name,due_on,due_at,completed,projects.name&completed_since=now",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "user_tasks_raw", type: "string" },
            {
              name: "data.length",
              customName: "user_task_count",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI conversational response
      aiNode(
        "4",
        "AI: Generate deadline response",
        760,
        "You are a friendly, helpful deadline management assistant. Based on the user's query and their actual task data from Asana, provide a conversational, informative response. List relevant deadlines, highlight upcoming ones, and offer helpful reminders. If they asked about a specific project or task, focus on that. Include time remaining for each deadline. Be concise but thorough. Respond JSON: {response_text: string, deadlines_summary: string, next_deadline_task: string, next_deadline_date: string, overdue_count: number}",
        "User: {user_name}. Query: {query_text}. Query type: {query_type}. Time frame: {time_frame}. Mentioned project: {mentioned_project}. Task data from Asana: {user_tasks_raw}. Total tasks: {user_task_count}.",
        [
          { field: "response_text", as: "bot_response", type: "string" },
          {
            field: "deadlines_summary",
            as: "deadlines_summary",
            type: "string",
          },
          {
            field: "next_deadline_task",
            as: "next_deadline_task",
            type: "string",
          },
          {
            field: "next_deadline_date",
            as: "next_deadline_date",
            type: "string",
          },
          { field: "overdue_count", as: "overdue_count", type: "number" },
        ],
      ),

      // 5. Reminder creation API
      n("5", "hitApi", "Create reminder in Asana", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Reminder: {next_deadline_task}","notes":"Auto-generated reminder for upcoming deadline: {next_deadline_date}\\nOriginal query: {query_text}","assignee":"{user_email}","due_on":"{next_deadline_date}","projects":["{asana_project_id}"]}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "reminder_task_id",
              type: "string",
            },
            {
              name: "data.permalink_url",
              customName: "reminder_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Proactive daily digest email
      emailNode(
        "6",
        "Send deadline digest email",
        1240,
        "{user_email}",
        "Your Deadline Update — {next_deadline_task} due {next_deadline_date}",
        "<p>Hi {user_name},</p><p>{bot_response}</p><h3>Your Deadlines</h3><p>{deadlines_summary}</p><p><strong>Next deadline:</strong> {next_deadline_task} — due {next_deadline_date}</p><p>Overdue tasks: {overdue_count}</p><p>A reminder has been created: <a href='{reminder_url}'>View in Asana</a></p>",
      ),

      // 7. Completion tracking (Sheets)
      sheetsNode(
        "7",
        "Log deadline query tracking (Sheets)",
        1480,
        "Deadline_Queries",
        '[["{user_name}","{user_email}","{query_type}","{query_text}","{user_task_count}","{next_deadline_task}","{next_deadline_date}","{overdue_count}","{reminder_task_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1322. DELIVERY EXCEPTION HANDLER (8 nodes)
  // ===================================================================

  "Delivery Exception Handler": {
    summary:
      "Captures shipping exception events via webhook, extracts exception details with AI, classifies the exception type in code, retrieves customer order context from HubSpot, sends a proactive customer notification email, routes based on resolution type, triggers replacement orders via API, and logs exception analytics to Sheets.",
    nodes: [
      trigger("Webhook: Shipping exception event", "webhook_app", {
        source: "shipping_carrier",
        event: "shipment.exception",
        howItStarts:
          "Fires when a shipping carrier reports a delivery exception (delay, damage, lost, address issue, weather, etc.) for an active shipment",
        setup:
          "Configure webhook from your shipping provider (ShipStation, EasyPost, AfterShip, etc.). Set up OpenAI API key for exception data extraction. Configure HubSpot for customer order lookup. Set up email for customer notifications. Configure API access for replacement order creation. Provide Google Sheets for exception analytics.",
        inputFields: [],
      }),

      // 2. AI extract exception data
      aiNode(
        "2",
        "AI: Extract exception details",
        280,
        "You are a logistics operations analyst. Parse the raw shipping exception webhook payload to extract the tracking number, carrier name, exception type, exception description, shipment status, estimated delivery date, recipient name, recipient email, recipient phone, order ID, and shipping address. Respond JSON: {tracking_number: string, carrier: string, exception_type: string, exception_description: string, shipment_status: string, estimated_delivery: string, recipient_name: string, recipient_email: string, recipient_phone: string, order_id: string, shipping_address: string}",
        "Shipping exception payload: {webhook_payload}",
        [
          { field: "tracking_number", as: "tracking_number", type: "string" },
          { field: "carrier", as: "carrier_name", type: "string" },
          { field: "exception_type", as: "exception_type", type: "string" },
          {
            field: "exception_description",
            as: "exception_description",
            type: "string",
          },
          { field: "shipment_status", as: "shipment_status", type: "string" },
          {
            field: "estimated_delivery",
            as: "estimated_delivery",
            type: "string",
          },
          { field: "recipient_name", as: "recipient_name", type: "string" },
          { field: "recipient_email", as: "recipient_email", type: "string" },
          { field: "recipient_phone", as: "recipient_phone", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "shipping_address", as: "shipping_address", type: "string" },
        ],
      ),

      // 3. Exception classification (runCode)
      n("3", "runCode", "Classify exception severity", 520, {
        runCodeDetails: {
          code: `// Classify exception type and determine resolution path
const exType = (variables.exception_type || '').toLowerCase();
const desc = (variables.exception_description || '').toLowerCase();
let severity = 'medium';
let resolutionPath = 'monitor';
let needsReplacement = false;
let customerImpact = 'delayed';
if (exType.includes('lost') || exType.includes('missing')) {
  severity = 'critical';
  resolutionPath = 'replacement';
  needsReplacement = true;
  customerImpact = 'order_lost';
} else if (exType.includes('damage') || desc.includes('damaged')) {
  severity = 'high';
  resolutionPath = 'replacement';
  needsReplacement = true;
  customerImpact = 'damaged_goods';
} else if (exType.includes('address') || exType.includes('undeliverable')) {
  severity = 'high';
  resolutionPath = 'address_correction';
  customerImpact = 'address_issue';
} else if (exType.includes('delay') || exType.includes('weather')) {
  severity = 'low';
  resolutionPath = 'monitor';
  customerImpact = 'delayed';
}
const exceptionId = 'EXC-' + (variables.order_id || '') + '-' + Date.now().toString(36).slice(-4).toUpperCase();
return { severity: severity, resolution_path: resolutionPath, needs_replacement: needsReplacement, customer_impact: customerImpact, exception_id: exceptionId };`,
          outputs: [
            { name: "severity", type: "string" },
            { name: "resolution_path", type: "string" },
            { name: "needs_replacement", type: "boolean" },
            { name: "customer_impact", type: "string" },
            { name: "exception_id", type: "string" },
          ],
        },
      }),

      // 4. Customer order context (HubSpot)
      hubspotGetNode(
        "4",
        "Lookup customer order in HubSpot",
        760,
        "crm/v3/objects/deals/search",
        [
          {
            name: "results[0].properties.dealname",
            customName: "deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "order_value",
            type: "string",
          },
          {
            name: "results[0].properties.hs_object_id",
            customName: "deal_id",
            type: "string",
          },
          {
            name: "results[0].properties.dealstage",
            customName: "deal_stage",
            type: "string",
          },
        ],
      ),

      // 5. Customer notification email
      emailNode(
        "5",
        "Send proactive customer notification",
        1000,
        "{recipient_email}",
        "Shipping Update for Order {order_id} — {exception_type}",
        "<p>Hi {recipient_name},</p><p>We wanted to proactively let you know about a shipping update for your order <strong>{order_id}</strong>.</p><p><strong>Status:</strong> {exception_type} — {exception_description}</p><p><strong>Carrier:</strong> {carrier_name} | Tracking: {tracking_number}</p><p><strong>Updated delivery estimate:</strong> {estimated_delivery}</p><p><strong>What we are doing:</strong> Our team is actively working on resolving this. Resolution path: {resolution_path}.</p><p>We apologize for any inconvenience and will keep you updated. Reference: {exception_id}</p>",
      ),

      // 6. Resolution routing condition
      n("6", "condition", "Check: Does order need replacement?", 1240, {
        conditionData: [
          { variable: "needs_replacement", operator: "equals", value: "true" },
        ],
      }),

      // 7. Replacement trigger API — True path
      n(
        "7",
        "hitApi",
        "Create replacement order",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.shipstation.com/orders/createorder",
            headers: [
              {
                key: "Authorization",
                value: "Basic {shipstation_api_key_base64}",
              },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "orderNumber", value: "REPL-{order_id}" },
              { key: "orderStatus", value: "awaiting_shipment" },
              {
                key: "shipTo",
                value:
                  '{"name":"{recipient_name}","street1":"{shipping_address}"}',
              },
              {
                key: "internalNotes",
                value:
                  "Replacement for {order_id}. Exception: {exception_type} ({exception_id}). Original tracking: {tracking_number}",
              },
            ],
            variables: [
              {
                name: "orderId",
                customName: "replacement_order_id",
                type: "string",
              },
              {
                name: "orderNumber",
                customName: "replacement_order_number",
                type: "string",
              },
            ],
          },
        },
        380,
      ),

      // 8. Exception analytics (Sheets) — Both paths converge
      sheetsNode(
        "8",
        "Log exception analytics (Sheets)",
        1480,
        "Exception_Analytics",
        '[["{exception_id}","{order_id}","{tracking_number}","{carrier_name}","{exception_type}","{severity}","{customer_impact}","{resolution_path}","{needs_replacement}","{replacement_order_id}","{recipient_email}","{order_value}","=NOW()"]]',
        -220,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1323. DELIVERY TIME WINDOW SELECTOR BOT (7 nodes)
  // ===================================================================

  "Delivery Time Window Selector Bot": {
    summary:
      "Receives delivery queries via webhook, extracts order data with AI, looks up order and logistics info via API, generates available delivery windows in code, presents options conversationally with AI, reserves the selected window via API, and sends confirmation via email and SMS.",
    nodes: [
      trigger("Webhook: Delivery window query", "webhook_app", {
        source: "chatbot",
        event: "delivery.window_request",
        howItStarts:
          "Fires when a customer asks about available delivery time windows for their order, either through a chatbot, website widget, or customer portal",
        setup:
          "Configure webhook from your customer-facing chat or portal. Set up OpenAI API key for data extraction and conversational responses. Configure order management API for logistics lookup. Set up delivery scheduling API for window reservation. Configure email and SMS for confirmations.",
        inputFields: [],
      }),

      // 2. AI extract order data
      aiNode(
        "2",
        "AI: Extract order and delivery context",
        280,
        "You are a delivery scheduling assistant. Parse the raw webhook payload to extract the customer name, email, phone, order ID, delivery address, preferred date range, any special instructions, and the current delivery status if available. Respond JSON: {customer_name: string, customer_email: string, customer_phone: string, order_id: string, delivery_address: string, preferred_date: string, special_instructions: string, current_status: string}",
        "Delivery window query payload: {webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "delivery_address", as: "delivery_address", type: "string" },
          { field: "preferred_date", as: "preferred_date", type: "string" },
          {
            field: "special_instructions",
            as: "special_instructions",
            type: "string",
          },
          { field: "current_status", as: "current_status", type: "string" },
        ],
      ),

      // 3. Order and logistics lookup API
      n("3", "hitApi", "Lookup order logistics details", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.orderplatform.com/v1/orders/{order_id}?include=shipments,logistics",
          headers: [
            { key: "Authorization", value: "Bearer {order_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "order.status",
              customName: "order_status",
              type: "string",
            },
            {
              name: "order.items_count",
              customName: "items_count",
              type: "number",
            },
            {
              name: "order.warehouse_location",
              customName: "warehouse_location",
              type: "string",
            },
            {
              name: "order.weight_kg",
              customName: "package_weight",
              type: "string",
            },
            {
              name: "order.delivery_zone",
              customName: "delivery_zone",
              type: "string",
            },
          ],
        },
      }),

      // 4. Available window generation (runCode)
      n("4", "runCode", "Generate available delivery windows", 760, {
        runCodeDetails: {
          code: `// Generate available delivery time windows based on logistics
const zone = variables.delivery_zone || 'standard';
const preferredDate = variables.preferred_date || '';
const now = new Date();
const windows = [];
// Generate windows for next 5 business days
for (let d = 1; d <= 5; d++) {
  const date = new Date(now);
  date.setDate(date.getDate() + d);
  if (date.getDay() === 0 || date.getDay() === 6) continue;
  const dateStr = date.toISOString().split('T')[0];
  windows.push({ id: 'W' + d + 'A', date: dateStr, time: '9:00 AM - 12:00 PM', type: 'morning' });
  windows.push({ id: 'W' + d + 'B', date: dateStr, time: '1:00 PM - 5:00 PM', type: 'afternoon' });
  windows.push({ id: 'W' + d + 'C', date: dateStr, time: '6:00 PM - 9:00 PM', type: 'evening' });
}
const windowCount = windows.length;
const windowSummary = windows.slice(0, 6).map(w => w.date + ' ' + w.time + ' (' + w.id + ')').join('; ');
const firstAvailable = windows.length > 0 ? windows[0].date + ' ' + windows[0].time : 'No windows available';
return { available_windows: JSON.stringify(windows), window_count: windowCount, window_summary: windowSummary, first_available: firstAvailable, selected_window_id: windows.length > 0 ? windows[0].id : '' };`,
          outputs: [
            { name: "available_windows", type: "string" },
            { name: "window_count", type: "number" },
            { name: "window_summary", type: "string" },
            { name: "first_available", type: "string" },
            { name: "selected_window_id", type: "string" },
          ],
        },
      }),

      // 5. AI conversational presentation
      aiNode(
        "5",
        "AI: Present delivery options",
        1000,
        "You are a friendly delivery scheduling assistant. Present the available delivery windows to the customer in a clear, conversational format. Highlight the earliest option and any that match their preferred date. Mention special instructions handling. Respond JSON: {response_message: string, recommended_window: string, recommended_window_id: string}",
        "Customer: {customer_name}. Order: {order_id} ({items_count} items, {package_weight}kg). Zone: {delivery_zone}. Preferred date: {preferred_date}. Special instructions: {special_instructions}. Available windows: {window_summary}. First available: {first_available}.",
        [
          {
            field: "response_message",
            as: "bot_delivery_response",
            type: "string",
          },
          {
            field: "recommended_window",
            as: "recommended_window",
            type: "string",
          },
          {
            field: "recommended_window_id",
            as: "recommended_window_id",
            type: "string",
          },
        ],
      ),

      // 6. Window reservation API
      n("6", "hitApi", "Reserve delivery window", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.orderplatform.com/v1/deliveries/schedule",
          headers: [
            { key: "Authorization", value: "Bearer {order_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "order_id", value: "{order_id}" },
            { key: "window_id", value: "{recommended_window_id}" },
            { key: "delivery_address", value: "{delivery_address}" },
            { key: "special_instructions", value: "{special_instructions}" },
            { key: "customer_email", value: "{customer_email}" },
          ],
          variables: [
            { name: "delivery.id", customName: "delivery_id", type: "string" },
            {
              name: "delivery.confirmed_window",
              customName: "confirmed_window",
              type: "string",
            },
            {
              name: "delivery.status",
              customName: "reservation_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Confirmation email + SMS
      emailNode(
        "7",
        "Send delivery confirmation email and SMS",
        1480,
        "{customer_email}",
        "Delivery Confirmed — Order {order_id} | {confirmed_window}",
        "<p>Hi {customer_name},</p><p>Your delivery for order <strong>{order_id}</strong> has been confirmed!</p><p><strong>Delivery Window:</strong> {confirmed_window}</p><p><strong>Address:</strong> {delivery_address}</p><p><strong>Special Instructions:</strong> {special_instructions}</p><p><strong>Delivery ID:</strong> {delivery_id}</p><p>We will send you a reminder the day before. You can reschedule up to 24 hours in advance.</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1324. DEMO REQUEST BOT (8 nodes)
  // ===================================================================

  "Demo Request Bot": {
    summary:
      "Captures demo inquiries via webhook, qualifies leads with AI, creates CRM leads in HubSpot, checks calendar availability via Calendly API, schedules the demo via API, enriches the lead with Clearbit company data, sends a sales rep briefing email, and routes no-shows via condition for follow-up.",
    nodes: [
      trigger("Webhook: Demo request submitted", "webhook_app", {
        source: "website",
        event: "form.demo_request",
        howItStarts:
          "Fires when a prospect submits a demo request form on the website, initiating lead qualification, scheduling, and sales preparation workflow",
        setup:
          "Configure webhook from your website form. Set up OpenAI API key for lead qualification. Configure HubSpot for CRM lead creation. Set up Calendly API for calendar availability. Configure Clearbit API for company enrichment. Set up email for sales rep briefings.",
        inputFields: [],
      }),

      // 2. AI lead qualification
      aiNode(
        "2",
        "AI: Qualify demo lead",
        280,
        "You are a B2B sales development specialist. Analyze the raw form submission payload and extract the lead details: name, email, company, job title, company size, use case, budget range, and timeline. Qualify the lead using BANT criteria (Budget, Authority, Need, Timeline). Score from 1-100 and categorize as hot, warm, or cold. Respond JSON: {lead_name: string, lead_email: string, lead_company: string, lead_title: string, company_size: string, use_case: string, budget_range: string, timeline: string, qualification_score: number, lead_temperature: string, disqualification_reason: string, ideal_demo_type: string, sales_notes: string}",
        "Demo request form data: {webhook_payload}",
        [
          { field: "lead_name", as: "lead_name", type: "string" },
          { field: "lead_email", as: "lead_email", type: "string" },
          { field: "lead_company", as: "lead_company", type: "string" },
          { field: "lead_title", as: "lead_title", type: "string" },
          { field: "company_size", as: "company_size", type: "string" },
          { field: "use_case", as: "use_case", type: "string" },
          { field: "budget_range", as: "budget_range", type: "string" },
          { field: "timeline", as: "timeline", type: "string" },
          {
            field: "qualification_score",
            as: "qualification_score",
            type: "number",
          },
          { field: "lead_temperature", as: "lead_temperature", type: "string" },
          { field: "ideal_demo_type", as: "ideal_demo_type", type: "string" },
          { field: "sales_notes", as: "sales_notes", type: "string" },
        ],
      ),

      // 3. CRM lead creation (HubSpot)
      hubspotPostNode(
        "3",
        "Create lead in HubSpot",
        520,
        "/crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"firstname":"{lead_name}","email":"{lead_email}","company":"{lead_company}","jobtitle":"{lead_title}","hs_lead_status":"NEW","lifecyclestage":"lead","notes":"Demo request | Score: {qualification_score} | Temp: {lead_temperature} | Use case: {use_case} | Budget: {budget_range} | Timeline: {timeline}"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_created_at",
            type: "string",
          },
        ],
      ),

      // 4. Calendar availability check (Calendly API)
      n("4", "hitApi", "Check Calendly availability", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.calendly.com/event_type_available_times?event_type={calendly_event_type_uri}&start_time={tomorrow_iso}&end_time={week_from_now_iso}",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "collection",
              customName: "available_slots_raw",
              type: "string",
            },
            {
              name: "collection[0].start_time",
              customName: "first_available_slot",
              type: "string",
            },
            {
              name: "collection.length",
              customName: "available_slot_count",
              type: "number",
            },
          ],
        },
      }),

      // 5. Demo scheduling API
      n("5", "hitApi", "Schedule demo via Calendly", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduled_events",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "event_type", value: "{calendly_event_type_uri}" },
            { key: "start_time", value: "{first_available_slot}" },
            {
              key: "invitee",
              value: '{"name":"{lead_name}","email":"{lead_email}"}',
            },
          ],
          variables: [
            {
              name: "resource.uri",
              customName: "scheduled_event_uri",
              type: "string",
            },
            {
              name: "resource.start_time",
              customName: "demo_scheduled_time",
              type: "string",
            },
            {
              name: "resource.location.join_url",
              customName: "demo_join_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Pre-demo enrichment (Clearbit API)
      n("6", "hitApi", "Enrich lead company with Clearbit", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={lead_company_domain}",
          headers: [{ key: "Authorization", value: "Bearer {clearbit_key}" }],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "enriched_employee_count",
              type: "number",
            },
            {
              name: "metrics.estimatedAnnualRevenue",
              customName: "enriched_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "enriched_industry",
              type: "string",
            },
            { name: "tech", customName: "enriched_tech_stack", type: "string" },
            {
              name: "description",
              customName: "enriched_company_desc",
              type: "string",
            },
          ],
        },
      }),

      // 7. Sales rep briefing email
      emailNode(
        "7",
        "Send sales rep demo briefing",
        1480,
        "{assigned_sales_rep_email}",
        "Demo Briefing: {lead_name} ({lead_company}) — {demo_scheduled_time}",
        "<h2>Demo Briefing</h2><p><strong>Lead:</strong> {lead_name} — {lead_title}</p><p><strong>Company:</strong> {lead_company} ({enriched_industry})</p><p><strong>Size:</strong> {enriched_employee_count} employees | Revenue: {enriched_revenue}</p><p><strong>Qualification:</strong> Score {qualification_score}/100 | Temp: {lead_temperature}</p><p><strong>Use Case:</strong> {use_case}</p><p><strong>Budget:</strong> {budget_range} | Timeline: {timeline}</p><p><strong>Demo Type:</strong> {ideal_demo_type}</p><hr><p><strong>Scheduled:</strong> {demo_scheduled_time}</p><p><strong>Join URL:</strong> <a href='{demo_join_url}'>{demo_join_url}</a></p><p><strong>Company Background:</strong> {enriched_company_desc}</p><p><strong>Tech Stack:</strong> {enriched_tech_stack}</p><p><strong>Sales Notes:</strong> {sales_notes}</p>",
      ),

      // 8. No-show handler condition
      n(
        "8",
        "condition",
        "Check: Is lead hot enough for priority follow-up?",
        1720,
        {
          conditionData: [
            {
              variable: "qualification_score",
              operator: "greater_than",
              value: "70",
            },
          ],
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1325. DEPLOY NOTIFICATION (7 nodes)
  // ===================================================================

  "Deploy Notification": {
    summary:
      "Captures CI/CD deployment events via webhook, extracts build data with AI, classifies the deployment status in code, posts a notification to Slack, routes failures for analysis via condition, tags the responsible team on Slack, and logs deployment tracking to Sheets.",
    nodes: [
      trigger("Webhook: CI/CD deployment event", "webhook_app", {
        source: "ci_cd_pipeline",
        event: "deployment.completed",
        howItStarts:
          "Fires when a CI/CD pipeline completes a deployment (GitHub Actions, Jenkins, CircleCI, etc.), capturing build status, commit details, and deployment metadata",
        setup:
          "Configure webhook from your CI/CD platform. Set up OpenAI API key for build data extraction. Configure Slack webhooks for deployment notifications and team tagging. Provide Google Sheets for deployment tracking and analytics.",
        inputFields: [],
      }),

      // 2. AI extract build data
      aiNode(
        "2",
        "AI: Extract deployment details",
        280,
        "You are a DevOps analyst. Parse the raw CI/CD webhook payload to extract the build ID, build status (success/failure/cancelled), repository name, branch, commit SHA, commit message, author name, author email, deployment environment (staging/production), build duration, artifact URL, and any error messages. Respond JSON: {build_id: string, build_status: string, repo_name: string, branch: string, commit_sha: string, commit_message: string, author_name: string, author_email: string, environment: string, build_duration_seconds: number, artifact_url: string, error_message: string}",
        "CI/CD deployment webhook payload: {webhook_payload}",
        [
          { field: "build_id", as: "build_id", type: "string" },
          { field: "build_status", as: "build_status", type: "string" },
          { field: "repo_name", as: "repo_name", type: "string" },
          { field: "branch", as: "branch", type: "string" },
          { field: "commit_sha", as: "commit_sha", type: "string" },
          { field: "commit_message", as: "commit_message", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
          { field: "environment", as: "environment", type: "string" },
          {
            field: "build_duration_seconds",
            as: "build_duration",
            type: "number",
          },
          { field: "artifact_url", as: "artifact_url", type: "string" },
          { field: "error_message", as: "error_message", type: "string" },
        ],
      ),

      // 3. Status classification (runCode)
      n("3", "runCode", "Classify deployment status", 520, {
        runCodeDetails: {
          code: `// Classify deployment and determine notification urgency
const status = (variables.build_status || '').toLowerCase();
const env = (variables.environment || '').toLowerCase();
const duration = Number(variables.build_duration) || 0;
const durationMin = (duration / 60).toFixed(1);
const isSuccess = status === 'success' || status === 'passed';
const isFailure = status === 'failure' || status === 'failed' || status === 'error';
const isProd = env === 'production' || env === 'prod';
const emoji = isSuccess ? ':white_check_mark:' : isFailure ? ':x:' : ':warning:';
const urgency = isFailure && isProd ? 'critical' : isFailure ? 'high' : isProd ? 'medium' : 'low';
const needsTeamTag = isFailure;
const shortSha = (variables.commit_sha || '').substring(0, 7);
return { is_success: isSuccess, is_failure: isFailure, is_production: isProd, status_emoji: emoji, urgency: urgency, needs_team_tag: needsTeamTag, duration_minutes: durationMin, short_sha: shortSha };`,
          outputs: [
            { name: "is_success", type: "boolean" },
            { name: "is_failure", type: "boolean" },
            { name: "is_production", type: "boolean" },
            { name: "status_emoji", type: "string" },
            { name: "urgency", type: "string" },
            { name: "needs_team_tag", type: "boolean" },
            { name: "duration_minutes", type: "string" },
            { name: "short_sha", type: "string" },
          ],
        },
      }),

      // 4. Slack notification
      slackNode(
        "4",
        "Post deployment notification (Slack)",
        760,
        "{status_emoji} *Deploy: {repo_name}/{branch} → {environment}*\n*Status:* {build_status} | *Build:* {build_id} | *Duration:* {duration_minutes}m\n*Commit:* `{short_sha}` — {commit_message}\n*Author:* {author_name}\n*Urgency:* {urgency}",
      ),

      // 5. Failure analysis condition
      n("5", "condition", "Check: Did deployment fail?", 1000, {
        conditionData: [
          { variable: "is_failure", operator: "equals", value: "true" },
        ],
      }),

      // 6. Team tagging Slack — True path (failure)
      slackNode(
        "6",
        "Tag team for deploy failure (Slack)",
        1240,
        ":rotating_light: *DEPLOY FAILURE — {environment}*\n*Repo:* {repo_name}/{branch}\n*Build:* {build_id} | Author: {author_name} ({author_email})\n*Error:* {error_message}\n*Commit:* `{short_sha}` — {commit_message}\n\n<!channel> Please investigate immediately. Artifact: {artifact_url}",
        380,
      ),

      // 7. Deployment tracking (Sheets) — Both paths converge
      sheetsNode(
        "7",
        "Log deployment tracking (Sheets)",
        1240,
        "Deployment_Log",
        '[["{build_id}","{repo_name}","{branch}","{environment}","{build_status}","{urgency}","{author_name}","{commit_message}","{short_sha}","{duration_minutes}","{error_message}","{artifact_url}","=NOW()"]]',
        -220,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1326. DOCUMENT APPROVAL WORKFLOW (8 nodes)
  // ===================================================================

  "Document Approval Workflow": {
    summary:
      "Receives document submissions via webhook, extracts document metadata with AI, determines the approval chain in code, sends approver notification emails, waits 3 days for review, checks review status via condition, handles revision cycles via email, and logs final approvals to Sheets.",
    nodes: [
      trigger("Webhook: Document submitted for approval", "webhook_app", {
        source: "document_system",
        event: "document.submitted",
        howItStarts:
          "Fires when a document (contract, proposal, policy, report, etc.) is submitted for approval through the document management system",
        setup:
          "Configure webhook from your document management system (Google Drive, SharePoint, Notion, etc.). Set up OpenAI API key for document metadata extraction. Configure email for approver notifications and revision handling. Provide Google Sheets for approval tracking.",
        inputFields: [],
      }),

      // 2. AI extract document data
      aiNode(
        "2",
        "AI: Extract document metadata",
        280,
        "You are a document management specialist. Parse the raw webhook payload to extract the document title, document type (contract, proposal, policy, report, budget, memo), author name, author email, document URL, department, priority level, summary of contents, and any specific approval requirements or notes. Respond JSON: {doc_title: string, doc_type: string, author_name: string, author_email: string, doc_url: string, department: string, priority: string, doc_summary: string, approval_notes: string, page_count: number, version: string}",
        "Document submission payload: {webhook_payload}",
        [
          { field: "doc_title", as: "doc_title", type: "string" },
          { field: "doc_type", as: "doc_type", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
          { field: "doc_url", as: "doc_url", type: "string" },
          { field: "department", as: "department", type: "string" },
          { field: "priority", as: "doc_priority", type: "string" },
          { field: "doc_summary", as: "doc_summary", type: "string" },
          { field: "approval_notes", as: "approval_notes", type: "string" },
          { field: "page_count", as: "page_count", type: "number" },
          { field: "version", as: "doc_version", type: "string" },
        ],
      ),

      // 3. Approval chain determination (runCode)
      n("3", "runCode", "Determine approval chain", 520, {
        runCodeDetails: {
          code: `// Determine approval chain based on document type and department
const docType = (variables.doc_type || '').toLowerCase();
const department = (variables.department || '').toLowerCase();
const priority = (variables.doc_priority || '').toLowerCase();
let approvers = [];
let approvalLevel = 'standard';
// Route based on document type
if (docType.includes('contract') || docType.includes('legal')) {
  approvers.push({ role: 'Legal Review', email: '{legal_approver_email}' });
  approvers.push({ role: 'Department Head', email: '{dept_head_email}' });
  approvalLevel = 'elevated';
} else if (docType.includes('budget') || docType.includes('financial')) {
  approvers.push({ role: 'Finance Review', email: '{finance_approver_email}' });
  approvers.push({ role: 'CFO', email: '{cfo_email}' });
  approvalLevel = 'elevated';
} else if (docType.includes('policy')) {
  approvers.push({ role: 'Compliance', email: '{compliance_email}' });
  approvers.push({ role: 'Executive', email: '{exec_approver_email}' });
  approvalLevel = 'executive';
} else {
  approvers.push({ role: 'Manager', email: '{manager_email}' });
  approvalLevel = 'standard';
}
if (priority === 'high' || priority === 'urgent') approvalLevel = 'expedited';
const primaryApprover = approvers[0] || { role: 'Manager', email: '{manager_email}' };
const approverChain = approvers.map(a => a.role).join(' → ');
const approvalId = 'APPR-' + Date.now().toString(36).toUpperCase();
return { primary_approver_email: primaryApprover.email, primary_approver_role: primaryApprover.role, approver_chain: approverChain, approval_level: approvalLevel, approver_count: approvers.length, approval_id: approvalId };`,
          outputs: [
            { name: "primary_approver_email", type: "string" },
            { name: "primary_approver_role", type: "string" },
            { name: "approver_chain", type: "string" },
            { name: "approval_level", type: "string" },
            { name: "approver_count", type: "number" },
            { name: "approval_id", type: "string" },
          ],
        },
      }),

      // 4. Approver notification email
      emailNode(
        "4",
        "Send approval request to reviewer",
        760,
        "{primary_approver_email}",
        "Approval Required ({doc_priority}): {doc_title} — {approval_id}",
        "<h2>Document Approval Request</h2><p><strong>Document:</strong> {doc_title} (v{doc_version})</p><p><strong>Type:</strong> {doc_type} | <strong>Priority:</strong> {doc_priority}</p><p><strong>Author:</strong> {author_name} ({department})</p><p><strong>Pages:</strong> {page_count} | <strong>Approval Level:</strong> {approval_level}</p><p><strong>Approval Chain:</strong> {approver_chain}</p><hr><p><strong>Summary:</strong> {doc_summary}</p><p><strong>Notes:</strong> {approval_notes}</p><p><strong>Document Link:</strong> <a href='{doc_url}'>Open Document</a></p><hr><p>Please review and respond within 3 business days. Reference: {approval_id}</p>",
      ),

      // 5. Wait 3 days for review
      n("5", "delayAction", "Wait 3 days for review", 1000, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      // 6. Review tracking condition
      n("6", "condition", "Check: Was document approved?", 1240, {
        conditionData: [
          {
            variable: "approval_status",
            operator: "equals",
            value: "approved",
          },
        ],
      }),

      // 7. Revision cycle handling email — False path (not approved / needs revision)
      emailNode(
        "7",
        "Send revision request to author",
        1480,
        "{author_email}",
        "Revision Requested: {doc_title} — {approval_id}",
        "<p>Hi {author_name},</p><p>Your document <strong>{doc_title}</strong> (v{doc_version}) requires revisions before approval.</p><p><strong>Reviewer:</strong> {primary_approver_role}</p><p><strong>Approval Chain:</strong> {approver_chain}</p><p>Please review the feedback, make the necessary changes, and resubmit.</p><p><strong>Document:</strong> <a href='{doc_url}'>Open Document</a></p><p>Reference: {approval_id}</p>",
        380,
      ),

      // 8. Final approval logging (Sheets) — True path (approved)
      sheetsNode(
        "8",
        "Log final approval (Sheets)",
        1480,
        "Document_Approvals",
        '[["{approval_id}","{doc_title}","{doc_type}","{doc_version}","{author_name}","{department}","{doc_priority}","{approval_level}","{approver_chain}","{primary_approver_role}","{approval_status}","=NOW()"]]',
        -220,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "8", "output_1"),
      e("6", "7", "output_2"),
    ],
  },

  // ===================================================================
  // 1327. DOCUMENT CLASSIFICATION AND FILING (7 nodes)
  // ===================================================================

  "Document Classification and Filing": {
    summary:
      "Receives documents via webhook intake, extracts content with AI, classifies the document type and category with AI, applies filing rules in code, files the document to the appropriate location via API, routes special actions based on classification, and logs the classification audit trail to Sheets.",
    nodes: [
      trigger("Webhook: Document intake received", "webhook_app", {
        source: "document_intake",
        event: "document.received",
        howItStarts:
          "Fires when a new document is received through any intake channel (email attachment, upload portal, scanner, API submission) and needs to be classified and filed automatically",
        setup:
          "Configure webhook from your document intake source (email parser, upload endpoint, scanner integration, etc.). Set up OpenAI API key for content extraction and classification. Configure Google Drive or SharePoint API for automated filing. Provide Google Sheets for classification audit trail.",
        inputFields: [],
      }),

      // 2. AI content extraction
      aiNode(
        "2",
        "AI: Extract document content",
        280,
        "You are a document processing specialist. Analyze the raw webhook payload to extract the document filename, file type (PDF, DOCX, image, etc.), file size, sender information (name, email), any OCR text content, document title if detectable, key entities mentioned (companies, people, dates, amounts), and the intake source. Respond JSON: {filename: string, file_type: string, file_size_kb: number, sender_name: string, sender_email: string, document_text: string, detected_title: string, key_entities: string, intake_source: string, intake_timestamp: string}",
        "Document intake payload: {webhook_payload}",
        [
          { field: "filename", as: "filename", type: "string" },
          { field: "file_type", as: "file_type", type: "string" },
          { field: "file_size_kb", as: "file_size_kb", type: "number" },
          { field: "sender_name", as: "sender_name", type: "string" },
          { field: "sender_email", as: "sender_email", type: "string" },
          { field: "document_text", as: "document_text", type: "string" },
          { field: "detected_title", as: "detected_title", type: "string" },
          { field: "key_entities", as: "key_entities", type: "string" },
          { field: "intake_source", as: "intake_source", type: "string" },
          { field: "intake_timestamp", as: "intake_timestamp", type: "string" },
        ],
      ),

      // 3. AI classification
      aiNode(
        "3",
        "AI: Classify document type",
        520,
        "You are a records management AI. Based on the extracted document content, classify it into a primary category (invoice, contract, proposal, report, policy, correspondence, receipt, legal_notice, hr_document, marketing_material, technical_spec, other) and a secondary category for more specific filing. Determine the confidentiality level (public, internal, confidential, restricted), retention period, and whether any immediate action is required. Respond JSON: {primary_category: string, secondary_category: string, confidentiality: string, retention_years: number, requires_action: boolean, action_description: string, classification_confidence: number, suggested_folder: string, tags: string}",
        "Filename: {filename}. File type: {file_type}. Detected title: {detected_title}. Key entities: {key_entities}. Sender: {sender_name} ({sender_email}). Document text excerpt: {document_text}.",
        [
          { field: "primary_category", as: "primary_category", type: "string" },
          {
            field: "secondary_category",
            as: "secondary_category",
            type: "string",
          },
          { field: "confidentiality", as: "confidentiality", type: "string" },
          { field: "retention_years", as: "retention_years", type: "number" },
          { field: "requires_action", as: "requires_action", type: "boolean" },
          {
            field: "action_description",
            as: "action_description",
            type: "string",
          },
          {
            field: "classification_confidence",
            as: "classification_confidence",
            type: "number",
          },
          { field: "suggested_folder", as: "suggested_folder", type: "string" },
          { field: "tags", as: "document_tags", type: "string" },
        ],
      ),

      // 4. Filing rules engine (runCode)
      n("4", "runCode", "Apply filing rules and determine destination", 760, {
        runCodeDetails: {
          code: `// Apply filing rules based on classification
const category = (variables.primary_category || '').toLowerCase();
const subCategory = variables.secondary_category || '';
const confidentiality = (variables.confidentiality || 'internal').toLowerCase();
const confidence = Number(variables.classification_confidence) || 0;
// Build folder path
let basePath = '/Shared Documents';
if (confidentiality === 'restricted') basePath = '/Restricted';
else if (confidentiality === 'confidential') basePath = '/Confidential';
const categoryFolders = {
  invoice: '/Finance/Invoices',
  contract: '/Legal/Contracts',
  proposal: '/Sales/Proposals',
  report: '/Reports',
  policy: '/Compliance/Policies',
  correspondence: '/Correspondence',
  receipt: '/Finance/Receipts',
  legal_notice: '/Legal/Notices',
  hr_document: '/HR/Documents',
  marketing_material: '/Marketing/Assets',
  technical_spec: '/Engineering/Specs',
};
const folderSuffix = categoryFolders[category] || '/General/' + (variables.suggested_folder || 'Unsorted');
const fullPath = basePath + folderSuffix;
const year = new Date().getFullYear();
const filingPath = fullPath + '/' + year;
const newFilename = category.toUpperCase() + '_' + (variables.detected_title || variables.filename || 'untitled').replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50) + '.' + (variables.file_type || 'pdf');
const needsReview = confidence < 70;
const filingId = 'FILE-' + Date.now().toString(36).toUpperCase();
return { filing_path: filingPath, new_filename: newFilename, needs_review: needsReview, filing_id: filingId, folder_suffix: folderSuffix };`,
          outputs: [
            { name: "filing_path", type: "string" },
            { name: "new_filename", type: "string" },
            { name: "needs_review", type: "boolean" },
            { name: "filing_id", type: "string" },
            { name: "folder_suffix", type: "string" },
          ],
        },
      }),

      // 5. Automated filing API (Google Drive)
      n("5", "hitApi", "File document to Google Drive", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart",
          headers: [
            { key: "Authorization", value: "Bearer {google_drive_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{new_filename}" },
            { key: "parents", value: '["{target_folder_id}"]' },
            {
              key: "description",
              value:
                "Auto-filed: {primary_category}/{secondary_category} | Confidence: {classification_confidence}% | Tags: {document_tags} | Ref: {filing_id}",
            },
            {
              key: "properties",
              value:
                '{"category":"{primary_category}","confidentiality":"{confidentiality}","retention_years":"{retention_years}","filing_id":"{filing_id}"}',
            },
          ],
          variables: [
            { name: "id", customName: "drive_file_id", type: "string" },
            {
              name: "webViewLink",
              customName: "drive_file_url",
              type: "string",
            },
          ],
        },
      }),

      // 6. Action routing condition
      n("6", "condition", "Check: Does document require action?", 1240, {
        conditionData: [
          { variable: "requires_action", operator: "equals", value: "true" },
        ],
      }),

      // 7. Classification audit trail (Sheets) — Both paths converge
      sheetsNode(
        "7",
        "Log classification audit (Sheets)",
        1480,
        "Document_Classification_Audit",
        '[["{filing_id}","{filename}","{file_type}","{file_size_kb}","{sender_name}","{sender_email}","{primary_category}","{secondary_category}","{confidentiality}","{classification_confidence}","{document_tags}","{filing_path}","{new_filename}","{drive_file_id}","{requires_action}","{action_description}","{retention_years}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7", "output_2"),
    ],
  },
};
